import { useState, useEffect, useMemo } from 'react';
import { formatCurrency, formatNumber } from '@/lib/utils';
import { exportAdminExcel } from '@/lib/adminExcelExport';
import { downloadFullReport, downloadPptx } from '@/lib/fullReportGenerator';
import type { FullReportData, ReportPriorityCard, ReportQuickWin, ReportRoadmap } from '@/lib/fullReportGenerator';
import { buildActionPlanEngine } from '@/lib/action-plan-engine';
import { buildSalesNarrative } from '@/lib/recommendation-engine-v2';
import { buildRecommendationMasterData } from '@/lib/build-recommendation-master-data';
import {
  exportSubmissionsToCSV,
  exportFunnelDataToCSV,
  exportFullAuditDataToJSON,
  exportAnalyticsToJSON
} from '@/lib/exportUtils';
import { auditQuestions } from '@/data/auditQuestions';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import {
  Download,
  Trash2,
  Eye,
  Calendar,
  Building2,
  User,
  Phone,
  TrendingUp,
  FileText,
  Search,
  Filter,
  BarChart3,
  Users,
  Target,
  Flame,
  MessageSquare,
  Clock,
  CheckCircle2,
  XCircle,
  AlertCircle,
  AlertTriangle,
  Plus,
  Copy,
  FileDown,
  Layers,
  Zap,
  BarChart2,
  PieChart,
  RefreshCw,
  Globe,
} from 'lucide-react';
import { Layout } from '@/components/Layout';
import { AdminHelp } from '@/components/AdminHelp';
// Removed: Old ActionRecommendations component (archived)
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Separator } from '@/components/ui/separator';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import type { CompanyProfile } from '@/lib/index';
import { AUDIT_CATEGORIES, ROUTE_PATHS } from '@/lib/index';
import type { MasterResult } from '@/lib/master-engine';
import { useAdminStore } from '@/hooks/useAdminStore';
import { useAdminDataStore } from '@/hooks/useAdminDataStore';
import { toast } from 'sonner';
import { supabase } from '@/integrations/supabase/client';

interface AuditSubmission {
  id: string;
  timestamp: string;
  companyProfile: CompanyProfile;
  // result kann MasterResult oder älteres AuditResult sein
  result: MasterResult & Record<string, any>;
  answers: Record<string, any>;
}

// ── Sichere Accessor-Helfer für MasterResult / AuditResult ──
function getMonthlyGap(r: any): number {
  return r?.computedMetrics?.monthlyRevenueGap
    ?? r?.views?.hero?.monthlyRevenueGap
    ?? 0;
}

function getAnnualGap(r: any): number {
  return r?.computedMetrics?.annualRevenueGap
    ?? r?.views?.hero?.annualRevenueGap
    ?? 0;
}

const statusConfig = {
  new: { label: 'Neu', color: 'bg-blue-500/10 text-blue-600 border-blue-500/20', icon: AlertCircle },
  contacted: { label: 'Kontaktiert', color: 'bg-purple-500/10 text-purple-600 border-purple-500/20', icon: Phone },
  qualified: { label: 'Qualifiziert', color: 'bg-amber-500/10 text-amber-600 border-amber-500/20', icon: CheckCircle2 },
  won: { label: 'Gewonnen', color: 'bg-green-500/10 text-green-600 border-green-500/20', icon: CheckCircle2 },
  lost: { label: 'Verloren', color: 'bg-gray-500/10 text-gray-600 border-gray-500/20', icon: XCircle },
};

const priorityConfig = {
  hot: { label: 'Hot', color: 'bg-red-500/10 text-red-600 border-red-500/20', icon: Flame },
  warm: { label: 'Warm', color: 'bg-orange-500/10 text-orange-600 border-orange-500/20', icon: TrendingUp },
  cold: { label: 'Cold', color: 'bg-blue-500/10 text-blue-600 border-blue-500/20', icon: Target },
};

export default function AdminDashboard() {
  const navigate = useNavigate();
  const { isAuthenticated } = useAdminStore();
  const { auditStatuses, setAuditStatus, setPriority, addNote, getAuditStatus } = useAdminDataStore();
  
  const [submissions, setSubmissions] = useState<AuditSubmission[]>([]);
  const [filteredSubmissions, setFilteredSubmissions] = useState<AuditSubmission[]>([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedSubmission, setSelectedSubmission] = useState<AuditSubmission | null>(null);
  const [showDetailDialog, setShowDetailDialog] = useState(false);
  const [showNoteDialog, setShowNoteDialog] = useState(false);
  const [newNote, setNewNote] = useState('');
  const [showCSVDialog, setShowCSVDialog] = useState(false);
  const [csvContent, setCSVContent] = useState('');
  
  // Filters
  const [dateFilter, setDateFilter] = useState<'all' | 'today' | 'week' | 'month'>('all');
  const [statusFilter, setStatusFilter] = useState<string>('all');
  const [priorityFilter, setPriorityFilter] = useState<string>('all');

  useEffect(() => {
    if (!isAuthenticated) {
      navigate(ROUTE_PATHS.ADMIN_LOGIN);
      return;
    }
    loadSubmissions();
  }, [isAuthenticated, navigate]);

  useEffect(() => {
    applyFilters();
  }, [submissions, searchTerm, dateFilter, statusFilter, priorityFilter]);

  const loadSubmissions = async () => {
    // ── Primär: Supabase (zentrale Datenbank) ──
    try {
      const { data: sbData, error: sbError } = await supabase
        .from('audit_submissions_2026_06_10')
        .select('*')
        .order('created_at', { ascending: false });

      if (!sbError && sbData && sbData.length > 0) {
        const mapped: AuditSubmission[] = sbData.map((row: Record<string, unknown>) => ({
          id: row.id as string,
          timestamp: row.created_at as string,
          companyProfile: {
            companyName: (row.company_name as string) || 'Unbekannt',
            companyDomain: (row.company_domain as string) || '',
            industry: (row.industry as string) || '',
            businessModel: ((row.business_model as string) || 'B2B') as 'B2B' | 'B2C' | 'Both',
            revenue: '',
            employees: '',
            teamSize: '',
            averageDealValue: '',
            salesCycle: '',
            hasCRM: false,
            hasMarketingAutomation: false,
            leadSources: [],
            contactFirstName: (row.contact_first_name as string) || '',
            contactLastName: (row.contact_last_name as string) || '',
            contactPosition: (row.contact_position as string) || '',
            contactEmail: (row.contact_email as string) || '',
            contactPhone: (row.contact_phone as string) || undefined,
            funnelData: {
              monthlyDeals: row.monthly_deals as number ?? null,
              averageDealSize: row.average_deal_size as number ?? null,
              salesCycleMonths: row.sales_cycle_months as number ?? null,
              monthlyContacts: row.monthly_contacts as number ?? null,
              monthlyRevenue: row.monthly_revenue as number ?? null,
              responseTime: null,
              followUpDiscipline: null,
              leadQuality: null,
              pipelineTransparency: null,
            },
          } as unknown as CompanyProfile,
          result: (row.result_json as MasterResult & Record<string, unknown>) || {} as MasterResult & Record<string, unknown>,
          answers: (row.raw_form as Record<string, unknown>) || {},
        }));
        setSubmissions(mapped);
        return;
      }
    } catch (_sbErr) {
      // Supabase nicht erreichbar → Fallback localStorage
    }

    // ── Fallback: localStorage (offline / alte Einträge) ──
    const allKeys = Object.keys(localStorage).filter(k => k.startsWith('audit-submission-'));
    const realKeys = allKeys.filter(k => k !== 'audit-submission-store-legacy');

    const loadedSubmissions: AuditSubmission[] = realKeys.map(key => {
      const data = localStorage.getItem(key);
      if (!data) return null;
      try { return JSON.parse(data); } catch { return null; }
    }).filter(Boolean) as AuditSubmission[];

    if (loadedSubmissions.length === 0) {
      try {
        const storeRaw = localStorage.getItem('salesforge-audit-storage');
        if (storeRaw) {
          const store = JSON.parse(storeRaw);
          const state = store?.state || store;
          if (state?.result && state?.companyProfile) {
            const cp = state.companyProfile as any;
            loadedSubmissions.push({
              id: 'audit-submission-store-legacy',
              timestamp: new Date().toISOString(),
              companyProfile: {
                companyName: cp.companyName || 'Unbekannt',
                industry: cp.industry || '',
                revenue: cp.revenue || '',
                employees: cp.employees || '',
                teamSize: cp.teamSize || '',
                businessModel: cp.businessModel || 'B2B',
                averageDealValue: cp.averageDealValue || '',
                salesCycle: cp.salesCycle || '',
                hasCRM: cp.hasCRM ?? false,
                crmName: cp.crmName,
                hasMarketingAutomation: cp.hasMarketingAutomation ?? false,
                leadSources: cp.leadSources || [],
                contactFirstName: cp.contactFirstName || '',
                contactLastName: cp.contactLastName || '',
                contactPosition: cp.contactPosition || '',
                contactEmail: cp.contactEmail || '',
                contactPhone: cp.contactPhone,
                funnelData: cp.funnelData,
                acquisitionMethods: cp.acquisitionMethods,
              },
              result: state.result as MasterResult & Record<string, any>,
              answers: state.rawAnswers || {},
            });
          }
        }
      } catch (err) {
        console.error('Fallback store read failed:', err);
      }
    }

    loadedSubmissions.sort((a, b) => new Date(b.timestamp).getTime() - new Date(a.timestamp).getTime());
    setSubmissions(loadedSubmissions);
  };

  const applyFilters = () => {
    let filtered = [...submissions];

    // Search filter
    if (searchTerm) {
      filtered = filtered.filter(sub =>
        sub.companyProfile.companyName.toLowerCase().includes(searchTerm.toLowerCase()) ||
        sub.companyProfile.contactFirstName.toLowerCase().includes(searchTerm.toLowerCase()) ||
        sub.companyProfile.contactLastName.toLowerCase().includes(searchTerm.toLowerCase()) ||
        sub.companyProfile.industry.toLowerCase().includes(searchTerm.toLowerCase())
      );
    }

    // Date filter
    if (dateFilter !== 'all') {
      const now = new Date();
      const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
      
      filtered = filtered.filter(sub => {
        const subDate = new Date(sub.timestamp);
        
        if (dateFilter === 'today') {
          return subDate >= today;
        } else if (dateFilter === 'week') {
          const weekAgo = new Date(today);
          weekAgo.setDate(weekAgo.getDate() - 7);
          return subDate >= weekAgo;
        } else if (dateFilter === 'month') {
          const monthAgo = new Date(today);
          monthAgo.setMonth(monthAgo.getMonth() - 1);
          return subDate >= monthAgo;
        }
        return true;
      });
    }

    // Status filter
    if (statusFilter !== 'all') {
      filtered = filtered.filter(sub => {
        const status = getAuditStatus(sub.id);
        return status.status === statusFilter;
      });
    }

    // Priority filter
    if (priorityFilter !== 'all') {
      filtered = filtered.filter(sub => {
        const status = getAuditStatus(sub.id);
        return status.priority === priorityFilter;
      });
    }

    setFilteredSubmissions(filtered);
  };

  // Analytics calculations
  const analytics = useMemo(() => {
    const now = new Date();
    const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
    const weekAgo = new Date(today);
    weekAgo.setDate(weekAgo.getDate() - 7);

    const todayCount = submissions.filter(s => new Date(s.timestamp) >= today).length;
    const weekCount = submissions.filter(s => new Date(s.timestamp) >= weekAgo).length;
    const hotLeads = submissions.filter(s => {
      const status = getAuditStatus(s.id);
      return status.priority === 'hot' || getMonthlyGap(s.result) > 50000;
    }).length;

    const statusCounts = {
      new: 0,
      contacted: 0,
      qualified: 0,
      won: 0,
      lost: 0,
    };

    submissions.forEach(s => {
      const status = getAuditStatus(s.id);
      statusCounts[status.status]++;
    });

    // Industry distribution
    const industries: Record<string, number> = {};
    submissions.forEach(s => {
      industries[s.companyProfile.industry] = (industries[s.companyProfile.industry] || 0) + 1;
    });

    return {
      todayCount,
      weekCount,
      hotLeads,
      statusCounts,
      industries,
    };
  }, [submissions, auditStatuses]);

  // ── Hilfsfunktion: FullReportData aus Submission bauen ──
  const buildReportDataFromSubmission = (sub: AuditSubmission): FullReportData | null => {
    try {
      const result = sub.result as any;
      const typedResult = result;
      const actionPlan = buildActionPlanEngine(typedResult);
      const narrative = buildSalesNarrative(typedResult);
      const masterData = buildRecommendationMasterData(typedResult);

      // priorityPlans → ReportPriorityCard
      const rPriorities: ReportPriorityCard[] = (actionPlan.priorityPlans || []).slice(0, 3).map((f: any, i: number) => ({
        rank: i + 1,
        title: f.title || '',
        problem: f.problem || f.impact || '',
        rootCause: f.rootCause || '',
        shortTermImpact: f.shortTermImpact || '',
        actions: f.actions || [],
        kpiContext: f.kpiContext,
        revenueLeakMonthly: f.revenueLeakMonthly,
        taskDetails: f.taskDetails,
        enrichedActions: f.enrichedActions,
      }));

      const rQuickWins: ReportQuickWin[] = (actionPlan.quickWins || []).slice(0, 4).map((q: any) => ({
        title: q.title || '',
        whyItWorks: q.whyItWorks || q.description || q.impact || '',
        implementation: q.implementation || q.action || '',
        owner: q.owner || 'Sales-Team',
        deadline: q.deadline || 'Innerhalb 14 Tage',
        successMetric: q.successMetric || q.metric || '',
        expectedImpact: q.expectedImpact,
      }));

      const rm = actionPlan.plan90Days;
      const rRoadmap: ReportRoadmap = {
        phase_1_0_30: {
          title: rm?.phase_1_0_30?.title || 'Tage 0–30: Stabilisieren',
          goal: rm?.phase_1_0_30?.goal || '',
          focus: rm?.phase_1_0_30?.focus || '',
          actions: rm?.phase_1_0_30?.actions || [],
        },
        phase_2_30_60: {
          title: rm?.phase_2_30_60?.title || 'Tage 30–60: Beschleunigen',
          goal: rm?.phase_2_30_60?.goal || '',
          focus: rm?.phase_2_30_60?.focus || '',
          actions: rm?.phase_2_30_60?.actions || [],
        },
        phase_3_60_90: {
          title: rm?.phase_3_60_90?.title || 'Tage 60–90: Skalieren',
          goal: rm?.phase_3_60_90?.goal || '',
          focus: rm?.phase_3_60_90?.focus || '',
          actions: rm?.phase_3_60_90?.actions || [],
        },
      };

      // Executive-Diagnose aus Narrative ableiten
      const fmtEurAdmin = (v: number) => new Intl.NumberFormat('de-DE', { style: 'currency', currency: 'EUR', maximumFractionDigits: 0 }).format(v || 0);
      const execHeadline = narrative?.hero?.headline
        || `${masterData?.dominantBottleneck?.title || 'Ihr Sales-Prozess'} ist der kritischste Engpass`;
      const execSummary = narrative?.rootCauseDetail
        || narrative?.rootCause
        || `Basierend auf dem Audit hat ${masterData?.companyName || 'Ihr Unternehmen'} ein monatliches Umsatzpotenzial von ${fmtEurAdmin(masterData?.monthlyLoss || 0)}.`;
      const sigSentence = narrative?.oneThing
        || `Starten Sie jetzt mit: ${masterData?.topPriorityTitle || rPriorities[0]?.title || '–'}`;

      return {
        result: typedResult,
        priorities: rPriorities,
        quickWins: rQuickWins,
        roadmap: rRoadmap,
        executiveDiagnosis: {
          headline: execHeadline,
          summary: execSummary,
        },
        signatureSentence: sigSentence,
      };
    } catch (err) {
      console.error('buildReportDataFromSubmission error:', err);
      return null;
    }
  };

  const handleDelete = (id: string) => {
    if (confirm('Möchten Sie diese Submission wirklich löschen?')) {
      // id IST bereits der vollständige localStorage-Key (z.B. 'audit-submission-1234567890')
      // oder der Legacy-Key 'audit-submission-store-legacy'
      if (id === 'audit-submission-store-legacy') {
        localStorage.removeItem('salesforge-audit-storage');
      } else {
        // id ist der vollständige Key — direkt löschen
        localStorage.removeItem(id);
      }
      loadSubmissions();
      toast.success('Submission gelöscht');
    }
  };

  const handleExportCSV = () => {
    
    if (filteredSubmissions.length === 0) {
      toast.error('❌ Keine Daten zum Exportieren vorhanden');
      return;
    }
    // Helper function to escape CSV values
    const escapeCSV = (value: unknown): string => {
      if (value === null || value === undefined) return '';
      const stringValue = String(value);
      // If value contains comma, semicolon, newline, or quotes, wrap in quotes and escape quotes
      if (stringValue.includes(',') || stringValue.includes(';') || stringValue.includes('\n') || stringValue.includes('"')) {
        return `"${stringValue.replace(/"/g, '""')}"`;
      }
      return stringValue;
    };

    const headers = [
      'Datum',
      'Firmenname',
      'Branche',
      'Umsatz',
      'Mitarbeiter',
      'Vorname',
      'Nachname',
      'Position',
      'Telefon',
      'E-Mail',
      'Umsatzverlust/Monat',
      'Umsatzverlust/Jahr',
      'Verlorene Deals/Monat',
      'Status',
      'Priorität',
    ];

    const rows = filteredSubmissions.map(sub => {
      const status = getAuditStatus(sub.id);
      return [
        escapeCSV(new Date(sub.timestamp).toLocaleString('de-DE')),
        escapeCSV(sub.companyProfile.companyName),
        escapeCSV(sub.companyProfile.industry),
        escapeCSV(sub.companyProfile.revenue),
        escapeCSV(sub.companyProfile.employees),
        escapeCSV(sub.companyProfile.contactFirstName),
        escapeCSV(sub.companyProfile.contactLastName),
        escapeCSV(sub.companyProfile.contactPosition),
        escapeCSV(sub.companyProfile.contactPhone || ''),
        escapeCSV(sub.companyProfile.contactEmail || ''),
        escapeCSV(getMonthlyGap(sub.result) > 0 ? formatCurrency(getMonthlyGap(sub.result)) : 'N/A'),
        escapeCSV(getAnnualGap(sub.result) > 0 ? formatCurrency(getAnnualGap(sub.result)) : 'N/A'),
        escapeCSV(sub.result?.normalizedData?.monthlyDeals != null ? formatNumber(sub.result.normalizedData.monthlyDeals) : 'N/A'),
        escapeCSV(statusConfig[status.status].label),
        escapeCSV(priorityConfig[status.priority].label),
      ];
    });

    // Add BOM for proper UTF-8 encoding in Excel
    const BOM = '\uFEFF';
    const csv = BOM + [headers.map(escapeCSV), ...rows].map(row => row.join(';')).join('\n');
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    
    // Better filename with timestamp
    const timestamp = new Date().toISOString().replace(/[:.]/g, '-').slice(0, -5);
    const filename = `salesforge-audit-export-${timestamp}.csv`;
    
    // Alternative download method - more reliable
    
    // Create download link
    const link = document.createElement('a');
    link.href = url;
    link.download = filename;
    link.style.display = 'none';
    
    document.body.appendChild(link);
    
    // Force click with timeout
    setTimeout(() => {
      link.click();
    }, 100);
    
    
    // Cleanup after download
    setTimeout(() => {
      if (link.parentNode) {
        document.body.removeChild(link);
      }
      URL.revokeObjectURL(url);
    }, 1000);
    
    // Detect OS for download path hint
    const userAgent = navigator.userAgent;
    let downloadPath = 'Downloads-Ordner';
    if (userAgent.includes('Win')) {
      downloadPath = 'C:\\Users\\[IhrName]\\Downloads';
    } else if (userAgent.includes('Mac')) {
      downloadPath = '/Users/[IhrName]/Downloads';
    } else if (userAgent.includes('Linux')) {
      downloadPath = '/home/[IhrName]/Downloads';
    }
    
    toast.success(
      `✅ CSV exportiert: ${filteredSubmissions.length} Einträge\n📁 Datei: ${filename}\n📂 Speicherort: ${downloadPath}`,
      {
        duration: 8000,
      }
    );
    
  };

  const handleCopyCSV = async () => {
    
    if (filteredSubmissions.length === 0) {
      toast.error('❌ Keine Daten zum Kopieren vorhanden');
      return;
    }

    const escapeCSV = (value: unknown): string => {
      if (value === null || value === undefined) return '';
      const stringValue = String(value);
      if (stringValue.includes(',') || stringValue.includes(';') || stringValue.includes('\n') || stringValue.includes('"')) {
        return `"${stringValue.replace(/"/g, '""')}"`;
      }
      return stringValue;
    };

    const headers = [
      'Datum',
      'Firmenname',
      'Branche',
      'Umsatz',
      'Mitarbeiter',
      'Vorname',
      'Nachname',
      'Position',
      'Telefon',
      'E-Mail',
      'Umsatzverlust/Monat',
      'Umsatzverlust/Jahr',
      'Verlorene Deals/Monat',
      'Status',
      'Priorität',
    ];

    const rows = filteredSubmissions.map(sub => {
      const status = getAuditStatus(sub.id);
      return [
        escapeCSV(new Date(sub.timestamp).toLocaleString('de-DE')),
        escapeCSV(sub.companyProfile.companyName),
        escapeCSV(sub.companyProfile.industry),
        escapeCSV(sub.companyProfile.revenue),
        escapeCSV(sub.companyProfile.employees),
        escapeCSV(sub.companyProfile.contactFirstName),
        escapeCSV(sub.companyProfile.contactLastName),
        escapeCSV(sub.companyProfile.contactPosition),
        escapeCSV(sub.companyProfile.contactPhone || ''),
        escapeCSV(sub.companyProfile.contactEmail || ''),
        escapeCSV(getMonthlyGap(sub.result) > 0 ? formatCurrency(getMonthlyGap(sub.result)) : 'N/A'),
        escapeCSV(getAnnualGap(sub.result) > 0 ? formatCurrency(getAnnualGap(sub.result)) : 'N/A'),
        escapeCSV(sub.result?.normalizedData?.monthlyDeals != null ? formatNumber(sub.result.normalizedData.monthlyDeals) : 'N/A'),
        escapeCSV(statusConfig[status.status].label),
        escapeCSV(priorityConfig[status.priority].label),
      ];
    });

    const csv = [headers.map(escapeCSV), ...rows].map(row => row.join(';')).join('\n');
    
    // Show CSV in dialog for manual copy
    setCSVContent(csv);
    setShowCSVDialog(true);
    
    toast.info(`📄 CSV-Daten bereit zum Kopieren\n📋 ${filteredSubmissions.length} Einträge`, {
      duration: 3000,
    });
  };

  const handleExportJSON = () => {
    const dataToExport = {
      exportDate: new Date().toISOString(),
      totalSubmissions: filteredSubmissions.length,
      submissions: filteredSubmissions.map(sub => ({
        ...sub,
        status: getAuditStatus(sub.id),
      })),
    };
    
    const json = JSON.stringify(dataToExport, null, 2);
    const blob = new Blob([json], { type: 'application/json;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    
    // Better filename with timestamp
    const timestamp = new Date().toISOString().replace(/[:.]/g, '-').slice(0, -5);
    const filename = `salesforge-audit-export-${timestamp}.json`;
    
    // Create and trigger download
    const link = document.createElement('a');
    link.href = url;
    link.download = filename;
    link.style.display = 'none';
    
    document.body.appendChild(link);
    link.click();
    
    // Cleanup after download
    setTimeout(() => {
      document.body.removeChild(link);
      URL.revokeObjectURL(url);
    }, 1000);
    
    toast.success(`✅ JSON exportiert: ${filteredSubmissions.length} Einträge\n📁 Datei: ${filename}`, {
      duration: 5000,
    });
  };

  // ── Excel Export Handler ────────────────────────────────────────────────────
  const handleExportExcel = async () => {
    if (filteredSubmissions.length === 0) {
      toast.error('❌ Keine Daten zum Exportieren vorhanden');
      return;
    }

    const loadingToast = toast.loading(
      `📊 Erstelle Excel-Report für ${filteredSubmissions.length} Submission(s)...`,
      { duration: 30000 }
    );

    try {
      const sLabels: Record<string,string> = {
        new: 'Neu', contacted: 'Kontaktiert', qualified: 'Qualifiziert',
        won: 'Gewonnen', lost: 'Verloren'
      };
      const pLabels: Record<string,string> = {
        hot: 'Hot 🔴', warm: 'Warm 🟠', cold: 'Cold 🔵'
      };

      await exportAdminExcel(
        filteredSubmissions,
        getAuditStatus,
        sLabels,
        pLabels
      );

      toast.dismiss(loadingToast);
      toast.success(
        `✅ Excel-Report erstellt! ${filteredSubmissions.length} Submissions, 7 Sheets (Übersicht, Conversion, Umsatz, Qualitative, Anomalien, Engine-Befunde, Rohdaten)`,
        { duration: 8000 }
      );
    } catch (err) {
      toast.dismiss(loadingToast);
      const msg = err instanceof Error ? err.message : 'Unbekannter Fehler';
      toast.error(`❌ Excel-Export fehlgeschlagen: ${msg}`);
    }
  };

  const handleAddNote = () => {
    if (!selectedSubmission || !newNote.trim()) return;
    
    addNote(selectedSubmission.id, newNote);
    setNewNote('');
    setShowNoteDialog(false);
    toast.success('Notiz hinzugefügt');
  };

  if (!isAuthenticated) {
    return null;
  }

  return (
    <Layout>
      <div className="min-h-screen bg-gradient-to-b from-background via-secondary/20 to-background">
        <div className="container mx-auto px-4 py-8">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
          >
            {/* Header */}
            <div className="flex items-center justify-between mb-8">
              <div>
                <h1 className="text-4xl font-bold mb-2">Admin Dashboard</h1>
                <p className="text-muted-foreground">
                  Verwalten Sie alle Audit-Submissions und Leads
                </p>
              </div>
              <div className="flex gap-2 flex-wrap">
                <Button variant="outline" onClick={loadSubmissions} title="Submissions neu laden">
                  <RefreshCw className="w-4 h-4 mr-2" />
                  Aktualisieren
                </Button>
                <Button
                  variant="default"
                  onClick={handleExportExcel}
                  className="bg-green-600 hover:bg-green-700 text-white"
                  title="Excel-Report mit 7 Sheets herunterladen"
                >
                  <FileDown className="w-4 h-4 mr-2" />
                  Excel Report
                </Button>
                <Button variant="outline" onClick={handleExportCSV}>
                  <Download className="w-4 h-4 mr-2" />
                  CSV Download
                </Button>
                <Button variant="outline" onClick={handleCopyCSV}>
                  <Copy className="w-4 h-4 mr-2" />
                  CSV Kopieren
                </Button>
                <Button variant="outline" onClick={handleExportJSON}>
                  <FileText className="w-4 h-4 mr-2" />
                  JSON Export
                </Button>
              </div>
            </div>

            <Tabs defaultValue="overview" className="space-y-6">
              <TabsList>
                <TabsTrigger value="overview">
                  <BarChart3 className="w-4 h-4 mr-2" />
                  Übersicht
                </TabsTrigger>
                <TabsTrigger value="submissions">
                  <Users className="w-4 h-4 mr-2" />
                  Submissions ({filteredSubmissions.length})
                </TabsTrigger>
                <TabsTrigger value="funnel">
                  <Target className="w-4 h-4 mr-2" />
                  Funnel-Daten
                </TabsTrigger>
                <TabsTrigger value="analytics">
                  <TrendingUp className="w-4 h-4 mr-2" />
                  Analytics
                </TabsTrigger>
                <TabsTrigger value="export">
                  <Download className="w-4 h-4 mr-2" />
                  Export
                </TabsTrigger>
              </TabsList>

              {/* Overview Tab */}
              <TabsContent value="overview" className="space-y-6">
                {/* KPI Cards */}
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
                  <Card>
                    <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                      <CardTitle className="text-sm font-medium">Heute</CardTitle>
                      <Calendar className="h-4 w-4 text-muted-foreground" />
                    </CardHeader>
                    <CardContent>
                      <div className="text-2xl font-bold">{analytics.todayCount}</div>
                      <p className="text-xs text-muted-foreground">
                        Neue Audits heute
                      </p>
                    </CardContent>
                  </Card>

                  <Card>
                    <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                      <CardTitle className="text-sm font-medium">Diese Woche</CardTitle>
                      <TrendingUp className="h-4 w-4 text-muted-foreground" />
                    </CardHeader>
                    <CardContent>
                      <div className="text-2xl font-bold">{analytics.weekCount}</div>
                      <p className="text-xs text-muted-foreground">
                        Audits in 7 Tagen
                      </p>
                    </CardContent>
                  </Card>


                  <Card>
                    <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                      <CardTitle className="text-sm font-medium">Hot Leads</CardTitle>
                      <Flame className="h-4 w-4 text-red-500" />
                    </CardHeader>
                    <CardContent>
                      <div className="text-2xl font-bold text-red-600">{analytics.hotLeads}</div>
                      <p className="text-xs text-muted-foreground">
                        Hohe Priorität oder &gt; 50k Lücke
                      </p>
                    </CardContent>
                  </Card>
                </div>

                {/* Status Overview */}
                <Card>
                  <CardHeader>
                    <CardTitle>Lead Status</CardTitle>
                    <CardDescription>Verteilung nach Bearbeitungsstatus</CardDescription>
                  </CardHeader>
                  <CardContent>
                    <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
                      {Object.entries(analytics.statusCounts).map(([status, count]) => {
                        const config = statusConfig[status as keyof typeof statusConfig];
                        const Icon = config.icon;
                        return (
                          <div key={status} className="text-center">
                            <div className={`inline-flex items-center justify-center w-12 h-12 rounded-full ${config.color} mb-2`}>
                              <Icon className="w-6 h-6" />
                            </div>
                            <div className="text-2xl font-bold">{count}</div>
                            <div className="text-sm text-muted-foreground">{config.label}</div>
                          </div>
                        );
                      })}
                    </div>
                  </CardContent>
                </Card>

              </TabsContent>

              {/* Submissions Tab */}
              <TabsContent value="submissions" className="space-y-6">
                {/* Filters */}
                <Card>
                  <CardHeader>
                    <CardTitle className="flex items-center gap-2">
                      <Filter className="w-5 h-5" />
                      Filter & Suche
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-4">
                      <div className="relative">
                        <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-muted-foreground w-4 h-4" />
                        <Input
                          placeholder="Suchen..."
                          value={searchTerm}
                          onChange={(e) => setSearchTerm(e.target.value)}
                          className="pl-10"
                        />
                      </div>

                      <Select value={dateFilter} onValueChange={(value: any) => setDateFilter(value)}>
                        <SelectTrigger>
                          <SelectValue placeholder="Zeitraum" />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="all">Alle</SelectItem>
                          <SelectItem value="today">Heute</SelectItem>
                          <SelectItem value="week">Diese Woche</SelectItem>
                          <SelectItem value="month">Dieser Monat</SelectItem>
                        </SelectContent>
                      </Select>

                      <Select value={statusFilter} onValueChange={setStatusFilter}>
                        <SelectTrigger>
                          <SelectValue placeholder="Status" />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="all">Alle Status</SelectItem>
                          <SelectItem value="new">Neu</SelectItem>
                          <SelectItem value="contacted">Kontaktiert</SelectItem>
                          <SelectItem value="qualified">Qualifiziert</SelectItem>
                          <SelectItem value="won">Gewonnen</SelectItem>
                          <SelectItem value="lost">Verloren</SelectItem>
                        </SelectContent>
                      </Select>

                      <Select value={priorityFilter} onValueChange={setPriorityFilter}>
                        <SelectTrigger>
                          <SelectValue placeholder="Priorität" />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="all">Alle Prioritäten</SelectItem>
                          <SelectItem value="hot">Hot</SelectItem>
                          <SelectItem value="warm">Warm</SelectItem>
                          <SelectItem value="cold">Cold</SelectItem>
                        </SelectContent>
                      </Select>

                    </div>
                  </CardContent>
                </Card>

                {/* Submissions Table */}
                <Card>
                  <CardHeader>
                    <CardTitle>Submissions ({filteredSubmissions.length})</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="overflow-x-auto">
                      <Table>
                        <TableHeader>
                          <TableRow>
                            <TableHead>Datum</TableHead>
                            <TableHead>Unternehmen</TableHead>
                            <TableHead>Ansprechpartner</TableHead>
                            <TableHead>Kontakt</TableHead>
                            <TableHead>Umsatzverlust/Monat</TableHead>
                            <TableHead>Status</TableHead>
                            <TableHead>Priorität</TableHead>
                            <TableHead className="text-right">Aktionen</TableHead>
                          </TableRow>
                        </TableHeader>
                        <TableBody>
                          {filteredSubmissions.length === 0 ? (
                            <TableRow>
                              <TableCell colSpan={9} className="text-center py-8 text-muted-foreground">
                                Keine Submissions gefunden
                              </TableCell>
                            </TableRow>
                          ) : (
                            filteredSubmissions.map((sub) => {
                              const status = getAuditStatus(sub.id);
                              const statusConf = statusConfig[status.status];
                              const priorityConf = priorityConfig[status.priority];
                              
                              return (
                                <TableRow key={sub.id} className="hover:bg-muted/50">
                                  <TableCell className="whitespace-nowrap">
                                    <div className="flex items-center gap-2">
                                      <Calendar className="w-4 h-4 text-muted-foreground" />
                                      {new Date(sub.timestamp).toLocaleDateString('de-DE', {
                                        day: '2-digit',
                                        month: '2-digit',
                                        year: 'numeric',
                                      })}
                                    </div>
                                    <div className="text-xs text-muted-foreground">
                                      {new Date(sub.timestamp).toLocaleTimeString('de-DE', {
                                        hour: '2-digit',
                                        minute: '2-digit',
                                      })}
                                    </div>
                                  </TableCell>
                                  <TableCell>
                                    <div className="flex items-center gap-2">
                                      <Building2 className="w-4 h-4 text-muted-foreground" />
                                      <div>
                                        <div className="font-medium">{sub.companyProfile.companyName}</div>
                                        <div className="text-xs text-muted-foreground">{sub.companyProfile.industry}</div>
                                      </div>
                                    </div>
                                  </TableCell>
                                  <TableCell>
                                    <div className="flex items-center gap-2">
                                      <User className="w-4 h-4 text-muted-foreground" />
                                      <div>
                                        <div className="font-medium">
                                          {sub.companyProfile.contactFirstName} {sub.companyProfile.contactLastName}
                                        </div>
                                        <div className="text-xs text-muted-foreground">{sub.companyProfile.contactPosition}</div>
                                      </div>
                                    </div>
                                  </TableCell>
                                  <TableCell>
                                    <div className="space-y-1">
                                      {sub.companyProfile.contactPhone && (
                                        <div className="flex items-center gap-2 text-sm">
                                          <Phone className="w-4 h-4 text-muted-foreground" />
                                          {sub.companyProfile.contactPhone}
                                        </div>
                                      )}
                                      {sub.companyProfile.contactEmail && (
                                        <div className="text-sm text-muted-foreground">
                                          {sub.companyProfile.contactEmail}
                                        </div>
                                      )}
                                    </div>
                                  </TableCell>
                                  <TableCell>
                                    {getMonthlyGap(sub.result) > 0 ? (
                                      <div>
                                        <div className="font-bold text-destructive">
                                          {formatCurrency(getMonthlyGap(sub.result))}
                                        </div>
                                        <div className="text-xs text-muted-foreground">
                                          {formatNumber(sub.result?.normalizedData?.monthlyDeals ?? 0)} Deals
                                        </div>
                                      </div>
                                    ) : (
                                      <div className="text-xs text-muted-foreground">Nicht berechnet</div>
                                    )}
                                  </TableCell>
                                  <TableCell>
                                    <Select
                                      value={status.status}
                                      onValueChange={(value: any) => setAuditStatus(sub.id, value)}
                                    >
                                      <SelectTrigger className="w-[140px]">
                                        <SelectValue />
                                      </SelectTrigger>
                                      <SelectContent>
                                        {Object.entries(statusConfig).map(([key, config]) => (
                                          <SelectItem key={key} value={key}>
                                            <div className="flex items-center gap-2">
                                              <config.icon className="w-4 h-4" />
                                              {config.label}
                                            </div>
                                          </SelectItem>
                                        ))}
                                      </SelectContent>
                                    </Select>
                                  </TableCell>
                                  <TableCell>
                                    <Select
                                      value={status.priority}
                                      onValueChange={(value: any) => setPriority(sub.id, value)}
                                    >
                                      <SelectTrigger className="w-[120px]">
                                        <SelectValue />
                                      </SelectTrigger>
                                      <SelectContent>
                                        {Object.entries(priorityConfig).map(([key, config]) => (
                                          <SelectItem key={key} value={key}>
                                            <div className="flex items-center gap-2">
                                              <config.icon className="w-4 h-4" />
                                              {config.label}
                                            </div>
                                          </SelectItem>
                                        ))}
                                      </SelectContent>
                                    </Select>
                                  </TableCell>
                                  <TableCell className="text-right">
                                    <div className="flex items-center justify-end gap-2">
                                      <Button
                                        variant="ghost"
                                        size="sm"
                                        onClick={() => {
                                          setSelectedSubmission(sub);
                                          setShowNoteDialog(true);
                                        }}
                                      >
                                        <MessageSquare className="w-4 h-4" />
                                      </Button>
                                      <Button
                                        variant="ghost"
                                        size="sm"
                                        onClick={() => {
                                          setSelectedSubmission(sub);
                                          setShowDetailDialog(true);
                                        }}
                                      >
                                        <Eye className="w-4 h-4" />
                                      </Button>
                                      <Button
                                        variant="ghost"
                                        size="sm"
                                        onClick={() => handleDelete(sub.id)}
                                      >
                                        <Trash2 className="w-4 h-4 text-destructive" />
                                      </Button>
                                    </div>
                                  </TableCell>
                                </TableRow>
                              );
                            })
                          )}
                        </TableBody>
                      </Table>
                    </div>
                  </CardContent>
                </Card>
              </TabsContent>

              {/* Funnel-Daten Tab */}
              <TabsContent value="funnel" className="space-y-6">
                <Card>
                  <CardHeader>
                    <CardTitle>Funnel-Daten Übersicht</CardTitle>
                    <CardDescription>
                      Alle Kunden mit Funnel-Analyse Daten
                    </CardDescription>
                  </CardHeader>
                  <CardContent>
                    <div className="space-y-6">
                      {filteredSubmissions
                        .filter(sub => sub.companyProfile?.funnelData)
                        .map((submission) => {
                          const funnel = submission.companyProfile!.funnelData!;
                          return (
                            <Card key={submission.id} className="border-primary/20">
                              <CardHeader>
                                <div className="flex items-center justify-between">
                                  <div>
                                    <CardTitle className="text-lg">
                                      {submission.companyProfile?.companyName || 'Unbekannt'}
                                    </CardTitle>
                                    <CardDescription>
                                      {submission.companyProfile?.industry} | {submission.companyProfile?.contactEmail}
                                    </CardDescription>
                                  </div>
                                  <Badge variant="outline">
                                    {new Date(submission.timestamp).toLocaleDateString('de-DE')}
                                  </Badge>
                                </div>
                              </CardHeader>
                              <CardContent>
                                <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                                  {/* Monatlicher Umsatz */}
                                  <div className="space-y-1">
                                    <p className="text-xs text-muted-foreground">Monatlicher Umsatz</p>
                                    <p className="text-lg font-bold">
                                      {funnel.monthlyRevenue 
                                        ? formatCurrency(funnel.monthlyRevenue)
                                        : funnel.monthlyRevenueUnknown 
                                        ? 'Nicht bekannt'
                                        : '-'
                                      }
                                    </p>
                                  </div>
                                  
                                  {/* Kontakte */}
                                  <div className="space-y-1">
                                    <p className="text-xs text-muted-foreground">Kontakte/Monat</p>
                                    <p className="text-lg font-bold">
                                      {funnel.monthlyContacts || (funnel.monthlyContactsUnknown ? 'Nicht bekannt' : '-')}
                                    </p>
                                  </div>
                                  
                                  {/* Erstgespräche */}
                                  <div className="space-y-1">
                                    <p className="text-xs text-muted-foreground">Erstgespräche/Monat</p>
                                    <p className="text-lg font-bold">
                                      {funnel.monthlyDiscoveryCalls || (funnel.monthlyDiscoveryCallsUnknown ? 'Nicht bekannt' : '-')}
                                    </p>
                                  </div>
                                  
                                  {/* Präsentationen */}
                                  <div className="space-y-1">
                                    <p className="text-xs text-muted-foreground">Präsentationen/Monat</p>
                                    <p className="text-lg font-bold">
                                      {funnel.monthlyPresentations || (funnel.monthlyPresentationsUnknown ? 'Nicht bekannt' : '-')}
                                    </p>
                                  </div>
                                  
                                  {/* Abschlüsse */}
                                  <div className="space-y-1">
                                    <p className="text-xs text-muted-foreground">Abschlüsse/Monat</p>
                                    <p className="text-lg font-bold">
                                      {funnel.monthlyDeals || (funnel.monthlyDealsUnknown ? 'Nicht bekannt' : '-')}
                                    </p>
                                  </div>
                                  
                                  {/* Deal-Größe */}
                                  <div className="space-y-1">
                                    <p className="text-xs text-muted-foreground">Durchschn. Deal-Größe</p>
                                    <p className="text-lg font-bold">
                                      {funnel.averageDealSize > 0
                                        ? formatCurrency(funnel.averageDealSize)
                                        : funnel.averageDealSizeUnknown 
                                        ? 'Nicht bekannt'
                                        : '-'
                                      }
                                    </p>
                                  </div>
                                  
                                  {/* Sales Cycle */}
                                  <div className="space-y-1">
                                    <p className="text-xs text-muted-foreground">Sales Cycle</p>
                                    <p className="text-lg font-bold">
                                      {funnel.salesCycleMonths > 0
                                        ? `${funnel.salesCycleMonths} Monate`
                                        : funnel.salesCycleMonthsUnknown 
                                        ? 'Nicht bekannt'
                                        : '-'
                                      }
                                    </p>
                                  </div>
                                  
                                  {/* Reaktionszeit */}
                                  <div className="space-y-1">
                                    <p className="text-xs text-muted-foreground">Reaktionszeit</p>
                                    <p className="text-sm font-medium">
                                      {funnel.responseTimeUnknown 
                                        ? 'Nicht bekannt'
                                        : funnel.responseTime === 'under-5min' ? 'Unter 5 Min'
                                        : funnel.responseTime === 'under-1h' ? 'Unter 1 Std'
                                        : funnel.responseTime === 'same-day' ? 'Gleicher Tag'
                                        : funnel.responseTime === '1-3days' ? '1-3 Tage'
                                        : 'Über 3 Tage'
                                      }
                                    </p>
                                  </div>
                                </div>
                                
                                <Separator className="my-4" />
                                
                                <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
                                  {/* Follow-up */}
                                  <div className="space-y-1">
                                    <p className="text-xs text-muted-foreground">Follow-up-Disziplin</p>
                                    <Badge variant="outline">
                                      {funnel.followUpDisciplineUnknown 
                                        ? 'Nicht bekannt'
                                        : funnel.followUpDiscipline === 'very-structured' ? 'Sehr strukturiert'
                                        : funnel.followUpDiscipline === 'partly-structured' ? 'Teilweise strukturiert'
                                        : funnel.followUpDiscipline === 'irregular' ? 'Unregelmäßig'
                                        : 'Selten'
                                      }
                                    </Badge>
                                  </div>
                                  
                                  {/* Lead-Qualität */}
                                  <div className="space-y-1">
                                    <p className="text-xs text-muted-foreground">Lead-Qualität</p>
                                    <Badge variant="outline">
                                      {funnel.leadQualityUnknown 
                                        ? 'Nicht bekannt'
                                        : funnel.leadQuality === 'very-high' ? 'Sehr hoch'
                                        : funnel.leadQuality === 'good' ? 'Gut'
                                        : funnel.leadQuality === 'mixed' ? 'Gemischt'
                                        : 'Schwach'
                                      }
                                    </Badge>
                                  </div>
                                  
                                  {/* Pipeline-Transparenz */}
                                  <div className="space-y-1">
                                    <p className="text-xs text-muted-foreground">Pipeline-Transparenz</p>
                                    <Badge variant="outline">
                                      {funnel.pipelineTransparencyUnknown 
                                        ? 'Nicht bekannt'
                                        : funnel.pipelineTransparency === 'full' ? 'Vollständig'
                                        : funnel.pipelineTransparency === 'partial' ? 'Teilweise'
                                        : funnel.pipelineTransparency === 'low' ? 'Niedrig'
                                        : 'Minimal'
                                      }
                                    </Badge>
                                  </div>
                                </div>
                              </CardContent>
                            </Card>
                          );
                        })}
                      
                      {filteredSubmissions.filter(sub => sub.companyProfile?.funnelData).length === 0 && (
                        <div className="text-center py-12 text-muted-foreground">
                          <Target className="w-12 h-12 mx-auto mb-4 opacity-50" />
                          <p>Keine Funnel-Daten vorhanden</p>
                        </div>
                      )}
                    </div>
                  </CardContent>
                </Card>
              </TabsContent>

              {/* Analytics Tab */}
              <TabsContent value="analytics" className="space-y-6">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  {/* Industry Distribution */}
                  <Card>
                    <CardHeader>
                      <CardTitle>Branchen-Verteilung</CardTitle>
                      <CardDescription>Anzahl Audits pro Branche</CardDescription>
                    </CardHeader>
                    <CardContent className="space-y-4">
                      {Object.entries(analytics.industries)
                        .sort(([, a], [, b]) => b - a)
                        .slice(0, 10)
                        .map(([industry, count]) => (
                          <div key={industry}>
                            <div className="flex items-center justify-between mb-2">
                              <span className="text-sm font-medium">{industry}</span>
                              <span className="text-sm text-muted-foreground">{count}</span>
                            </div>
                            <div className="w-full bg-secondary rounded-full h-2">
                              <div
                                className="bg-primary h-2 rounded-full transition-all"
                                style={{ width: `${(count / submissions.length) * 100}%` }}
                              />
                            </div>
                          </div>
                        ))}
                    </CardContent>
                  </Card>

                </div>
              </TabsContent>
              
              {/* Export Tab */}
              <TabsContent value="export" className="space-y-6">

                {/* ── Excel Report ── */}
                <Card className="border-green-500/30 bg-green-500/5">
                  <CardHeader>
                    <CardTitle className="flex items-center gap-2 text-green-700">
                      <FileDown className="w-5 h-5" />
                      📊 Excel-Report (Empfohlen)
                    </CardTitle>
                    <CardDescription>
                      Vollständiger strukturierter Bericht mit 7 Sheets: Übersicht, Conversion-Analyse,
                      Umsatz-Potenzial, Qualitative Signale, Anomalien, Engine-Befunde und Rohdaten.
                    </CardDescription>
                  </CardHeader>
                  <CardContent>
                    <div className="flex items-center gap-4">
                      <Button
                        onClick={handleExportExcel}
                        className="bg-green-600 hover:bg-green-700 text-white"
                        size="lg"
                      >
                        <FileDown className="w-5 h-5 mr-2" />
                        Excel-Report herunterladen ({filteredSubmissions.length} Einträge)
                      </Button>
                      <div className="text-sm text-muted-foreground space-y-1">
                        <p>📋 7 strukturierte Sheets</p>
                        <p>🎨 Farbcodierte Ampel-Bewertung</p>
                        <p>⚠ Automatische Anomalie-Erkennung</p>
                        <p>📁 Kompatibel mit Excel, LibreOffice, Google Sheets</p>
                      </div>
                    </div>
                  </CardContent>
                </Card>

                <Card>
                  <CardHeader>
                    <CardTitle>Weitere Export-Formate</CardTitle>
                    <CardDescription>
                      Exportieren Sie Ihre Audit-Daten in verschiedenen Formaten
                    </CardDescription>
                  </CardHeader>
                  <CardContent className="space-y-6">
                    {/* CSV Exports */}
                    <div>
                      <h3 className="font-semibold mb-3 flex items-center gap-2">
                        <FileText className="w-4 h-4 text-primary" />
                        CSV-Exporte
                      </h3>
                      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                        <Button
                          variant="outline"
                          className="justify-start h-auto py-4"
                          onClick={() => exportSubmissionsToCSV(submissions)}
                        >
                          <div className="text-left">
                            <div className="font-semibold">Alle Submissions</div>
                            <div className="text-xs text-muted-foreground mt-1">
                              Unternehmen, Kontakt, Umsatzlücke
                            </div>
                          </div>
                        </Button>
                        
                        <Button
                          variant="outline"
                          className="justify-start h-auto py-4"
                          onClick={() => exportFunnelDataToCSV(submissions)}
                        >
                          <div className="text-left">
                            <div className="font-semibold">Funnel-Daten</div>
                            <div className="text-xs text-muted-foreground mt-1">
                              Kontakte, Conversion, Deals
                            </div>
                          </div>
                        </Button>
                        
                      </div>
                    </div>
                    
                    {/* JSON Exports */}
                    <div>
                      <h3 className="font-semibold mb-3 flex items-center gap-2">
                        <FileText className="w-4 h-4 text-primary" />
                        JSON-Exporte
                      </h3>
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <Button
                          variant="outline"
                          className="justify-start h-auto py-4"
                          onClick={() => exportFullAuditDataToJSON(submissions)}
                        >
                          <div className="text-left">
                            <div className="font-semibold">Komplette Audit-Daten</div>
                            <div className="text-xs text-muted-foreground mt-1">
                              Alle Daten inkl. Antworten (JSON)
                            </div>
                          </div>
                        </Button>
                        
                        <Button
                          variant="outline"
                          className="justify-start h-auto py-4"
                          onClick={() => exportAnalyticsToJSON(submissions)}
                        >
                          <div className="text-left">
                            <div className="font-semibold">Analytics-Daten</div>
                            <div className="text-xs text-muted-foreground mt-1">
                              Branchen-Benchmarks, Statistiken
                            </div>
                          </div>
                        </Button>
                      </div>
                    </div>
                    
                    {/* Info */}
                    <div className="p-4 bg-muted/50 border border-border rounded-lg">
                      <h4 className="font-semibold text-sm mb-2 flex items-center gap-2">
                        <AlertCircle className="w-4 h-4 text-primary" />
                        Hinweise zum Export
                      </h4>
                      <ul className="text-xs text-muted-foreground space-y-1">
                        <li>• CSV-Dateien können in Excel, Google Sheets oder anderen Tabellenkalkulationen geöffnet werden</li>
                        <li>• JSON-Dateien eignen sich für API-Integrationen oder Backup-Zwecke</li>
                        <li>• Alle Exporte enthalten nur die aktuell gefilterten Daten</li>
                        <li>• Datenschutz: Exportierte Dateien enthalten sensible Kundendaten</li>
                      </ul>
                    </div>
                  </CardContent>
                </Card>
              </TabsContent>
            </Tabs>
          </motion.div>
        </div>
      </div>

      {/* Detail Dialog */}
      <Dialog open={showDetailDialog} onOpenChange={setShowDetailDialog}>
        <DialogContent className="max-w-4xl max-h-[80vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Audit Details</DialogTitle>
            <DialogDescription>
              Vollständige Informationen zur Submission
            </DialogDescription>
          </DialogHeader>
          
          {selectedSubmission && (
            <Tabs defaultValue="details" className="w-full">
              <TabsList className="grid w-full grid-cols-3 mb-6">
                <TabsTrigger value="details">Übersicht & Kontakt</TabsTrigger>
                <TabsTrigger value="results">Ergebnisse</TabsTrigger>
                <TabsTrigger value="recommendations">Handlungsempfehlungen</TabsTrigger>
              </TabsList>
              
              <TabsContent value="details" className="space-y-6">
              {/* Company Info */}
              <div>
                <h3 className="font-semibold mb-3 flex items-center gap-2">
                  <Building2 className="w-5 h-5" />
                  Unternehmensinformationen
                </h3>
                <div className="grid grid-cols-2 gap-4 text-sm">
                  <div>
                    <span className="text-muted-foreground">Firmenname:</span>
                    <p className="font-medium">{selectedSubmission.companyProfile.companyName}</p>
                  </div>
                  <div>
                    <span className="text-muted-foreground">Branche:</span>
                    <p className="font-medium">{selectedSubmission.companyProfile.industry}</p>
                  </div>
                  {(selectedSubmission.companyProfile as any).companyDomain && (
                    <div className="col-span-2">
                      <span className="text-muted-foreground">Domain / Website:</span>
                      <p className="font-medium flex items-center gap-2">
                        <Globe className="w-3 h-3 text-muted-foreground" />
                        {(selectedSubmission.companyProfile as any).companyDomain}
                      </p>
                    </div>
                  )}
                  <div>
                    <span className="text-muted-foreground">Business Model:</span>
                    <p className="font-medium">{selectedSubmission.companyProfile.businessModel}</p>
                  </div>
                  <div>
                    <span className="text-muted-foreground">Team-Größe (Reps):</span>
                    <p className="font-medium">{selectedSubmission.companyProfile.teamSize || '–'}</p>
                  </div>
                  <div>
                    <span className="text-muted-foreground">CRM:</span>
                    <p className="font-medium">{selectedSubmission.companyProfile.hasCRM ? (selectedSubmission.companyProfile.crmName || 'Ja') : 'Kein CRM'}</p>
                  </div>
                  <div>
                    <span className="text-muted-foreground">Marketing Automation:</span>
                    <p className="font-medium">{selectedSubmission.companyProfile.hasMarketingAutomation ? 'Ja' : 'Nein'}</p>
                  </div>
                </div>
              </div>

              <Separator />

              {/* Contact Info */}
              <div>
                <h3 className="font-semibold mb-3 flex items-center gap-2">
                  <User className="w-5 h-5" />
                  Ansprechpartner
                </h3>
                <div className="grid grid-cols-2 gap-4 text-sm">
                  <div>
                    <span className="text-muted-foreground">Name:</span>
                    <p className="font-medium">
                      {selectedSubmission.companyProfile.contactFirstName}{' '}
                      {selectedSubmission.companyProfile.contactLastName}
                    </p>
                  </div>
                  <div>
                    <span className="text-muted-foreground">Position:</span>
                    <p className="font-medium">{selectedSubmission.companyProfile.contactPosition}</p>
                  </div>
                  {selectedSubmission.companyProfile.contactPhone && (
                    <div>
                      <span className="text-muted-foreground">Telefon:</span>
                      <p className="font-medium">{selectedSubmission.companyProfile.contactPhone}</p>
                    </div>
                  )}
                  <div>
                    <span className="text-muted-foreground">E-Mail:</span>
                    <p className="font-medium">{selectedSubmission.companyProfile.contactEmail || 'Nicht angegeben'}</p>
                  </div>
                </div>
              </div>

              {/* Funnel Data */}
              {selectedSubmission.companyProfile.funnelData && (
                <>
                  <Separator />
                  <div>
                    <h3 className="font-semibold mb-3 flex items-center gap-2">
                      <Target className="w-5 h-5" />
                      Funnel-Daten
                    </h3>
                    <div className="grid grid-cols-2 md:grid-cols-3 gap-4 text-sm">
                      <div>
                        <span className="text-muted-foreground">Monatlicher Umsatz:</span>
                        <p className="font-medium">
                          {selectedSubmission.companyProfile.funnelData.monthlyRevenue
                            ? formatCurrency(selectedSubmission.companyProfile.funnelData.monthlyRevenue)
                            : selectedSubmission.companyProfile.funnelData.monthlyRevenueUnknown
                            ? 'Nicht bekannt'
                            : '-'}
                        </p>
                      </div>
                      <div>
                        <span className="text-muted-foreground">Kontakte/Monat:</span>
                        <p className="font-medium">
                          {selectedSubmission.companyProfile.funnelData.monthlyContacts || (selectedSubmission.companyProfile.funnelData.monthlyContactsUnknown ? 'Nicht bekannt' : '-')}
                        </p>
                      </div>
                      <div>
                        <span className="text-muted-foreground">Erstgespräche/Monat:</span>
                        <p className="font-medium">
                          {selectedSubmission.companyProfile.funnelData.monthlyDiscoveryCalls || (selectedSubmission.companyProfile.funnelData.monthlyDiscoveryCallsUnknown ? 'Nicht bekannt' : '-')}
                        </p>
                      </div>
                      <div>
                        <span className="text-muted-foreground">Präsentationen/Monat:</span>
                        <p className="font-medium">
                          {selectedSubmission.companyProfile.funnelData.monthlyPresentations || (selectedSubmission.companyProfile.funnelData.monthlyPresentationsUnknown ? 'Nicht bekannt' : '-')}
                        </p>
                      </div>
                      <div>
                        <span className="text-muted-foreground">Abschlüsse/Monat:</span>
                        <p className="font-medium">
                          {selectedSubmission.companyProfile.funnelData.monthlyDeals || (selectedSubmission.companyProfile.funnelData.monthlyDealsUnknown ? 'Nicht bekannt' : '-')}
                        </p>
                      </div>
                      <div>
                        <span className="text-muted-foreground">Durchschn. Deal-Größe:</span>
                        <p className="font-medium">
                          {selectedSubmission.companyProfile.funnelData.averageDealSize > 0
                            ? formatCurrency(selectedSubmission.companyProfile.funnelData.averageDealSize)
                            : selectedSubmission.companyProfile.funnelData.averageDealSizeUnknown
                            ? 'Nicht bekannt'
                            : '-'}
                        </p>
                      </div>
                      <div>
                        <span className="text-muted-foreground">Sales Cycle:</span>
                        <p className="font-medium">
                          {selectedSubmission.companyProfile.funnelData.salesCycleMonths > 0
                            ? `${selectedSubmission.companyProfile.funnelData.salesCycleMonths} Monate`
                            : selectedSubmission.companyProfile.funnelData.salesCycleMonthsUnknown
                            ? 'Nicht bekannt'
                            : '-'}
                        </p>
                      </div>
                      <div>
                        <span className="text-muted-foreground">Reaktionszeit:</span>
                        <p className="font-medium">
                          {selectedSubmission.companyProfile.funnelData.responseTimeUnknown
                            ? 'Nicht bekannt'
                            : selectedSubmission.companyProfile.funnelData.responseTime === 'under-5min' ? 'Unter 5 Min'
                            : selectedSubmission.companyProfile.funnelData.responseTime === 'under-1h' ? 'Unter 1 Std'
                            : selectedSubmission.companyProfile.funnelData.responseTime === 'same-day' ? 'Gleicher Tag'
                            : selectedSubmission.companyProfile.funnelData.responseTime === '1-3days' ? '1-3 Tage'
                            : 'Über 3 Tage'}
                        </p>
                      </div>
                      <div>
                        <span className="text-muted-foreground">Follow-up-Disziplin:</span>
                        <p className="font-medium">
                          {selectedSubmission.companyProfile.funnelData.followUpDisciplineUnknown
                            ? 'Nicht bekannt'
                            : selectedSubmission.companyProfile.funnelData.followUpDiscipline === 'very-structured' ? 'Sehr strukturiert'
                            : selectedSubmission.companyProfile.funnelData.followUpDiscipline === 'partly-structured' ? 'Teilweise strukturiert'
                            : selectedSubmission.companyProfile.funnelData.followUpDiscipline === 'irregular' ? 'Unregelmäßig'
                            : 'Selten'}
                        </p>
                      </div>
                      <div>
                        <span className="text-muted-foreground">Lead-Qualität:</span>
                        <p className="font-medium">
                          {selectedSubmission.companyProfile.funnelData.leadQualityUnknown
                            ? 'Nicht bekannt'
                            : selectedSubmission.companyProfile.funnelData.leadQuality === 'very-high' ? 'Sehr hoch'
                            : selectedSubmission.companyProfile.funnelData.leadQuality === 'good' ? 'Gut'
                            : selectedSubmission.companyProfile.funnelData.leadQuality === 'mixed' ? 'Gemischt'
                            : 'Schwach'}
                        </p>
                      </div>
                      <div>
                        <span className="text-muted-foreground">Pipeline-Transparenz:</span>
                        <p className="font-medium">
                          {selectedSubmission.companyProfile.funnelData.pipelineTransparencyUnknown
                            ? 'Nicht bekannt'
                            : selectedSubmission.companyProfile.funnelData.pipelineTransparency === 'full' ? 'Vollständig'
                            : selectedSubmission.companyProfile.funnelData.pipelineTransparency === 'partial' ? 'Teilweise'
                            : selectedSubmission.companyProfile.funnelData.pipelineTransparency === 'low' ? 'Niedrig'
                            : 'Minimal'}
                        </p>
                      </div>
                    </div>
                  </div>
                </>
              )}

{/* Acquisition Methods */}
              {(() => {
                const acqMethods = (selectedSubmission.companyProfile as any).acquisitionMethods ||
                  selectedSubmission.result?.acquisitionMethods;
                if (!acqMethods) return null;
                const total = (acqMethods.inbound || 0) + (acqMethods.outbound || 0) + (acqMethods.partners || 0) + (acqMethods.existing || 0) + (acqMethods.referrals || 0) + (acqMethods.events || 0);
                if (total === 0) return null;
                const channels = [
                  { label: 'Inbound (Marketing)', key: 'inbound', color: 'bg-blue-500' },
                  { label: 'Outbound (Cold)', key: 'outbound', color: 'bg-violet-500' },
                  { label: 'Partner/Netzwerk', key: 'partners', color: 'bg-emerald-500' },
                  { label: 'Bestandskunden', key: 'existing', color: 'bg-amber-500' },
                  { label: 'Empfehlungen', key: 'referrals', color: 'bg-pink-500' },
                  { label: 'Events/Messen', key: 'events', color: 'bg-cyan-500' },
                ].filter(c => (acqMethods[c.key] || 0) > 0);
                return (
                  <>
                    <Separator />
                    <div>
                      <h3 className="font-semibold mb-4 flex items-center gap-2">
                        <PieChart className="w-5 h-5" />
                        Akquise-Mix
                      </h3>
                      <div className="space-y-2">
                        {channels.map(c => (
                          <div key={c.key}>
                            <div className="flex items-center justify-between mb-1">
                              <span className="text-sm">{c.label}</span>
                              <span className="text-sm font-bold">{acqMethods[c.key] || 0}%</span>
                            </div>
                            <div className="w-full bg-muted rounded-full h-2">
                              <div className={`${c.color} h-2 rounded-full`} style={{ width: `${Math.min(acqMethods[c.key] || 0, 100)}%` }} />
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  </>
                );
              })()}

              <Separator />

              {/* Hinweis auf Tab Ergebnisse */}
              <div className="bg-primary/5 border border-primary/20 rounded-xl p-4 flex items-center gap-4">
                <div className="w-10 h-10 rounded-full bg-primary/10 flex items-center justify-center flex-shrink-0">
                  <TrendingUp className="w-5 h-5 text-primary" />
                </div>
                <div className="flex-1">
                  <p className="font-semibold text-sm">Vollständige Analyse im Tab <strong>Ergebnisse</strong> einsehbar</p>
                  <p className="text-xs text-muted-foreground mt-0.5">Umsatzlücke, Funnel-Metriken und Fokusfelder</p>
                </div>
              </div>



              <Separator />

              {/* Notes */}
              <div>
                <h3 className="font-semibold mb-3 flex items-center gap-2">
                  <MessageSquare className="w-5 h-5" />
                  Notizen
                </h3>
                <div className="space-y-2">
                  {getAuditStatus(selectedSubmission.id).notes.length === 0 ? (
                    <p className="text-sm text-muted-foreground">Keine Notizen vorhanden</p>
                  ) : (
                    getAuditStatus(selectedSubmission.id).notes.map((note) => (
                      <div key={note.id} className="p-3 bg-muted rounded-lg">
                        <p className="text-sm">{note.note}</p>
                        <p className="text-xs text-muted-foreground mt-1">
                          {new Date(note.createdAt).toLocaleString('de-DE')}
                        </p>
                      </div>
                    ))
                  )}
                </div>
              </div>

              <Separator />

              {/* Questions & Answers */}
              <div>
                <h3 className="font-semibold mb-3 flex items-center gap-2">
                  <FileText className="w-5 h-5" />
                  Fragen & Antworten
                </h3>
                <div className="space-y-6">
                  {AUDIT_CATEGORIES.map((category) => {
                    const categoryQuestions = auditQuestions.filter(
                      (q) => q.categoryId === category.id
                    );
                    const answeredQuestions = categoryQuestions.filter(
                      (q) => selectedSubmission.answers[q.id] !== undefined
                    );

                    if (answeredQuestions.length === 0) return null;

                    return (
                      <div key={category.id} className="space-y-3">
                        <div className="flex items-center gap-2 pb-2 border-b">
                          <div className="w-8 h-8 rounded-full bg-primary/10 flex items-center justify-center">
                            <span className="text-sm font-bold text-primary">
                              {answeredQuestions.length}
                            </span>
                          </div>
                          <h4 className="font-semibold text-lg">{category.name}</h4>
                        </div>
                        <div className="space-y-2">
                          {answeredQuestions.map((question) => {
                            const answer = selectedSubmission.answers[question.id];
                            return (
                              <div
                                key={question.id}
                                className="p-3 bg-muted/50 rounded-lg space-y-1"
                              >
                                <p className="text-sm font-medium">{question.text}</p>
                                <div className="flex items-center gap-2">
                                  <span className="text-xs text-muted-foreground">Antwort:</span>
                                  {question.answerType === 'scale' ? (
                                    <div className="flex items-center gap-1">
                                      {[1, 2, 3, 4, 5].map((value) => (
                                        <div
                                          key={value}
                                          className={`w-6 h-6 rounded-full flex items-center justify-center text-xs font-bold ${
                                            value <= answer
                                              ? 'bg-primary text-primary-foreground'
                                              : 'bg-secondary text-muted-foreground'
                                          }`}
                                        >
                                          {value}
                                        </div>
                                      ))}
                                      <span className="ml-2 text-sm font-bold text-primary">
                                        {answer} / 5
                                      </span>
                                    </div>
                                  ) : question.answerType === 'boolean' ? (
                                    <Badge
                                      variant="outline"
                                      className={answer ? 'bg-green-500/10 text-green-600' : 'bg-red-500/10 text-red-600'}
                                    >
                                      {answer ? 'Ja' : 'Nein'}
                                    </Badge>
                                  ) : (
                                    <span className="text-sm font-medium">{answer}</span>
                                  )}
                                </div>
                              </div>
                            );
                          })}
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
              </TabsContent>

              {/* ── TAB 2: Ergebnisse ── */}
              <TabsContent value="results" className="space-y-6">
              {(() => {
                const r = selectedSubmission.result as any;
                const m = r?.computedMetrics;
                const tp = r?.teamPerformance;
                const sc = Array.isArray(r?.structuralCauses) ? r.structuralCauses : [];
                const fp = Array.isArray(r?.focusPriorities) ? r.focusPriorities : [];
                const ra = r?.rawAnswers || selectedSubmission.answers || {};
                const _getRaw = (key: string) => { const v = ra[key]; if (v == null) return null; const n = Number(v); return Number.isFinite(n) ? n : null; };
                const qualifiedLeadRate = _getRaw('qualifiedLeadRatePercentExact');
                const proposalsWithNextStep = _getRaw('proposalsWithNextStepPercentExact');
                const stalePipelineShare = _getRaw('stalePipelineSharePercentExact');
                const decisionMakerFirstMeeting = _getRaw('decisionMakerInFirstMeetingPercentExact');
                const pctFmt = (v?: number | null, d = 1) => (Number.isFinite(Number(v)) ? Number(v).toFixed(d) + '%' : '–');
                const eurFmt = (v?: number | null) => formatCurrency(v ?? 0);
                const rankColors = ['bg-red-500','bg-amber-500','bg-emerald-500'];
                return (
                  <>
                    {/* ── Revenue Hebel (direkt) ── */}
                    {/* ── Revenue Hebel ── */}
                    {m && (m.monthlyRevenueGap > 0 || m.annualRevenueGap > 0) && (
                      <div className="grid grid-cols-2 gap-4">
                        <div className="bg-destructive/5 border border-destructive/20 rounded-xl p-4">
                          <p className="text-xs font-bold uppercase tracking-wider text-muted-foreground mb-1">Monatliche Umsatzlücke</p>
                          <p className="text-3xl font-black text-destructive">{eurFmt(m.monthlyRevenueGap)}</p>
                          <p className="text-xs text-muted-foreground mt-1">Differenz Ist vs. Benchmark-Potenzial pro Monat</p>
                        </div>
                        <div className="bg-amber-500/5 border border-amber-500/20 rounded-xl p-4">
                          <p className="text-xs font-bold uppercase tracking-wider text-muted-foreground mb-1">Jahres-Gap</p>
                          <p className="text-3xl font-black text-amber-600">{eurFmt(m.annualRevenueGap)}</p>
                          <p className="text-xs text-muted-foreground mt-1">Potenzial auf Jahresbasis</p>
                        </div>
                      </div>
                    )}

                    {/* ── Top Levers ── */}
                    {r?.topLevers && r.topLevers.length > 0 && (
                      <div className="border border-border rounded-xl overflow-hidden">
                        <div className="bg-muted/40 px-4 py-3 border-b border-border">
                          <h3 className="font-semibold text-sm flex items-center gap-2">
                            <Flame className="w-4 h-4 text-amber-500" />
                            Top Hebel
                          </h3>
                          <p className="text-xs text-muted-foreground mt-0.5">Die größten Potenziale zur Umsatzsteigerung</p>
                        </div>
                        <div className="p-4 space-y-3">
                          {r.topLevers.map((lever: any, idx: number) => (
                            <div key={idx} className="bg-muted/50 rounded-lg p-3">
                              <div className="flex items-center justify-between mb-1">
                                <p className="font-semibold text-sm">{lever.title}</p>
                                <span className="text-sm font-bold text-emerald-600">+{formatCurrency(lever.monthlyImpact)}/Monat</span>
                              </div>
                              <p className="text-xs text-muted-foreground">{lever.whyItMatters}</p>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}

                    {/* ── 1. Aktuelle Umsatzrealität (teamPerformance) ── */}
                    {tp && tp.totalRevenueTargetYear > 0 && (
                      <div className="border border-border rounded-xl overflow-hidden">
                        <div className="bg-muted/40 px-4 py-3 border-b border-border">
                          <h3 className="font-semibold text-sm flex items-center gap-2">
                            <Target className="w-4 h-4" />
                            1. Aktuelle Umsatzrealität
                          </h3>
                          <p className="text-xs text-muted-foreground mt-0.5">Jahresziel, erreichter Stand und Forecast bei aktueller Pace</p>
                        </div>
                        <div className="p-4 grid grid-cols-2 gap-3">
                          {[
                            { label: 'Jahresziel Sales', value: eurFmt(tp.totalRevenueTargetYear), sub: 'Definiertes Umsatzziel für das laufende Jahr' },
                            { label: 'Bisher erzielt (YTD)', value: eurFmt(tp.revenueAchievedYTD), sub: 'Tatsächlich realisierter Umsatz' },
                            { label: 'Lineares Soll bis heute', value: eurFmt(tp.expectedRevenueByNow), sub: 'Erwarteter Umsatz bei linearem Verlauf' },
                            { label: 'Forecast-Gap', value: eurFmt(tp.projectedYearEndGap), sub: 'Voraussichtliche Ziellücke zum Jahresende', highlight: true },
                          ].map(kpi => (
                            <div key={kpi.label} className={`rounded-lg p-3 ${kpi.highlight ? 'bg-destructive/5 border border-destructive/20' : 'bg-muted/50'}`}>
                              <p className="text-xs text-muted-foreground">{kpi.label}</p>
                              <p className={`text-xl font-black mt-1 ${kpi.highlight ? 'text-destructive' : 'text-foreground'}`}>{kpi.value}</p>
                              <p className="text-xs text-muted-foreground mt-1">{kpi.sub}</p>
                            </div>
                          ))}
                        </div>
                        <div className="px-4 pb-4 grid grid-cols-2 gap-3">
                          {[
                            { label: 'Abs. Zielerreichung', value: pctFmt(tp.absoluteGoalAttainmentPct), sub: 'Bisher erzielt / Jahresziel' },
                            { label: 'Pace vs. Plan', value: pctFmt(tp.pacingVsPlanPct), sub: 'Bisher erzielt / lineares Soll', warn: (tp.pacingVsPlanPct || 0) < 90 },
                            { label: 'Rep Health Score', value: `${tp.repHealthScore ?? '–'}/100`, sub: 'Zielsteuerung, Pipeline, Execution' },
                            { label: 'Projektion Jahresende', value: eurFmt(tp.projectedYearEndRevenue), sub: 'Bei unveränderter Pace' },
                          ].map(kpi => (
                            <div key={kpi.label} className={`rounded-lg p-3 ${(kpi as any).warn ? 'bg-amber-500/5 border border-amber-500/20' : 'bg-muted/50'}`}>
                              <p className="text-xs text-muted-foreground">{kpi.label}</p>
                              <p className={`text-xl font-black mt-1 ${(kpi as any).warn ? 'text-amber-700' : 'text-foreground'}`}>{kpi.value}</p>
                              <p className="text-xs text-muted-foreground mt-1">{kpi.sub}</p>
                            </div>
                          ))}
                        </div>
                        {tp.summarySignals && (
                          <div className="mx-4 mb-4 rounded-lg bg-slate-50 dark:bg-slate-900 border border-border p-3 space-y-1">
                            {tp.summarySignals.hasPacingProblem && <p className="text-xs text-amber-700">⚠️ Das Team liegt unter dem linearen Sollverlauf. Ohne Gegensteuerung wird das Jahresziel voraussichtlich verfehlt.</p>}
                            {tp.summarySignals.hasTargetBreakdownProblem && <p className="text-xs text-amber-700">⚠️ Das Umsatzziel ist nicht konsequent auf Reps heruntergebrochen. Das erschwert Steuerung und Review.</p>}
                            {tp.summarySignals.hasPipelineProblem && <p className="text-xs text-amber-700">⚠️ Ein Teil der Reps verfügt nicht über ausreichend belastbare Pipeline. Strukturelles Risiko.</p>}
                            {tp.summarySignals.hasVarianceProblem && <p className="text-xs text-amber-700">⚠️ Hohe Leistungsstreuung im Team. Standardisierung von Top-Performer-Verhalten hebt den Durchschnitt.</p>}
                            {tp.summarySignals.hasReviewProblem && <p className="text-xs text-amber-700">⚠️ Review-Rhythmus oder Steuerungsdisziplin deuten auf ein Management-Thema hin.</p>}
                            {!tp.summarySignals.hasPacingProblem && !tp.summarySignals.hasTargetBreakdownProblem && !tp.summarySignals.hasPipelineProblem && (
                              <p className="text-xs text-emerald-700">✅ Keine kritischen Warnsignale bei Pace und Zielsteuerung erkannt.</p>
                            )}
                          </div>
                        )}
                      </div>
                    )}

                    {/* ── Unit Economics ── */}
                    {tp?.unitEconomics && (
                      <div className="border border-border rounded-xl overflow-hidden">
                        <div className="bg-muted/40 px-4 py-3 border-b border-border">
                          <h3 className="font-semibold text-sm flex items-center gap-2">
                            <TrendingUp className="w-4 h-4" />
                            Unit Economics
                          </h3>
                          <p className="text-xs text-muted-foreground mt-0.5">Effizienz des Vertriebsteams</p>
                        </div>
                        <div className="p-4 grid grid-cols-2 gap-3">
                          <div className="rounded-lg p-3 bg-muted/50">
                            <p className="text-xs text-muted-foreground">Umsatz / € Gehalt</p>
                            <p className="text-xl font-black mt-1 text-foreground">{formatCurrency(tp.unitEconomics.revenuePerEuroSalary)}</p>
                            <p className="text-xs text-muted-foreground mt-1">Ziel: {formatCurrency(tp.unitEconomics.benchmarkRevenuePerEuroSalary)}</p>
                          </div>
                          <div className="rounded-lg p-3 bg-muted/50">
                            <p className="text-xs text-muted-foreground">Gehaltskosten / Jahr</p>
                            <p className="text-xl font-black mt-1 text-foreground">{formatCurrency(tp.unitEconomics.totalSalaryCost)}</p>
                            <p className="text-xs text-muted-foreground mt-1">inkl. aller produktiven Reps</p>
                          </div>
                        </div>
                      </div>
                    )}

                    {/* ── 2. Strukturelle Ursachen ── */}
                    {sc.length > 0 && (
                      <div className="border border-border rounded-xl overflow-hidden">
                        <div className="bg-muted/40 px-4 py-3 border-b border-border">
                          <h3 className="font-semibold text-sm flex items-center gap-2">
                            <AlertTriangle className="w-4 h-4 text-amber-500" />
                            2. Strukturelle Ursachen
                          </h3>
                          <p className="text-xs text-muted-foreground mt-0.5">Warum die Performance aktuell hinter dem Ziel bleibt</p>
                        </div>
                        <div className="p-4 space-y-3">
                          {sc.map((cause: any, idx: number) => (
                            <div key={cause.id || idx} className={`rounded-xl border p-4 ${
                              cause.severity === 'high' ? 'border-destructive/30 bg-destructive/5' :
                              cause.severity === 'medium' ? 'border-amber-400/30 bg-amber-50/40 dark:bg-amber-900/10' :
                              'border-emerald-400/30 bg-emerald-50/40 dark:bg-emerald-900/10'
                            }`}>
                              <div className="flex items-start justify-between gap-3 flex-wrap">
                                <div className="flex items-center gap-2">
                                  <span className={`w-6 h-6 rounded-full flex items-center justify-center text-xs font-bold text-white flex-shrink-0 ${
                                    cause.severity === 'high' ? 'bg-destructive' : cause.severity === 'medium' ? 'bg-amber-500' : 'bg-emerald-500'
                                  }`}>{idx + 1}</span>
                                  <p className="font-semibold text-sm">{cause.title}</p>
                                </div>
                                <div className="flex items-center gap-2 flex-wrap">
                                  <span className={`inline-flex items-center rounded-full px-2.5 py-1 text-xs font-semibold ${
                                    cause.severity === 'good' ? 'bg-emerald-100 text-emerald-700' :
                                    cause.severity === 'medium' ? 'bg-amber-100 text-amber-700' :
                                    cause.severity === 'high' ? 'bg-red-100 text-red-700' :
                                    'bg-slate-100 text-slate-600'
                                  }`}>
                                    {cause.severity === 'good' ? 'stark' : cause.severity === 'medium' ? 'mittel' : cause.severity === 'high' ? 'kritisch' : 'offen'}
                                  </span>
                                  {cause.revenueImpactMonthly > 0 && (
                                    <span className="text-xs bg-muted px-2 py-1 rounded-full text-muted-foreground">~{eurFmt(cause.revenueImpactMonthly)}/Monat</span>
                                  )}
                                </div>
                              </div>
                              {cause.metricLabel && cause.metricValue && (
                                <div className="mt-2 inline-flex items-center gap-1 rounded-md bg-background/60 border border-border px-2 py-1">
                                  <span className="text-xs text-muted-foreground">{cause.metricLabel}:</span>
                                  <span className="text-xs font-semibold">{cause.metricValue}</span>
                                </div>
                              )}
                              <p className="mt-2 text-xs text-foreground/80">{cause.summary}</p>
                              {cause.businessImpact && <p className="mt-1 text-xs text-muted-foreground"><span className="font-medium text-foreground">Geschäftliche Folge:</span> {cause.businessImpact}</p>}
                            </div>
                          ))}
                        </div>
                      </div>
                    )}

                    {/* ── 3. Funnel-Konversionen & Benchmarkvergleich ── */}
                    {m && (
                      <div className="border border-border rounded-xl overflow-hidden">
                        <div className="bg-muted/40 px-4 py-3 border-b border-border">
                          <h3 className="font-semibold text-sm flex items-center gap-2">
                            <BarChart2 className="w-4 h-4" />
                            3. Funnel-Konversionen &amp; Benchmarkvergleich
                          </h3>
                        </div>
                        <div className="p-4 space-y-3">
                          {[
                            { label: 'Kontakt → Discovery', ist: m.convContactToDiscovery, soll: m.benchmarkContactToDiscovery },
                            { label: 'Discovery → Präsentation', ist: m.convDiscoveryToPresentation, soll: m.benchmarkDiscoveryToPresentation },
                            { label: 'Präsentation → Deal', ist: m.convPresentationToDeal, soll: m.benchmarkPresentationToDeal },
                          ].map(kpi => {
                            const ok = (kpi.ist ?? 0) >= (kpi.soll ?? 0);
                            return (
                              <div key={kpi.label}>
                                <div className="flex items-center justify-between mb-1">
                                  <p className="text-sm font-medium">{kpi.label}</p>
                                  <div className="flex items-center gap-3">
                                    <span className={`text-sm font-black ${ok ? 'text-emerald-600' : 'text-destructive'}`}>{pctFmt(kpi.ist)}</span>
                                    <span className="text-xs text-muted-foreground">Benchmark: {pctFmt(kpi.soll)}</span>
                                    <Badge variant="outline" className={ok ? 'bg-emerald-500/10 text-emerald-700 border-emerald-500/30 text-xs' : 'bg-destructive/10 text-destructive border-destructive/30 text-xs'}>
                                      {ok ? 'stark' : 'Lücke'}
                                    </Badge>
                                  </div>
                                </div>
                                <div className="w-full bg-secondary rounded-full h-2">
                                  <div className={`${ok ? 'bg-emerald-500' : 'bg-destructive'} h-2 rounded-full`} style={{ width: `${Math.min(kpi.ist ?? 0, 100)}%` }} />
                                </div>
                              </div>
                            );
                          })}
                        </div>
                      </div>
                    )}

                    {/* ── 4. Operative KPIs ── */}
                    {(qualifiedLeadRate !== null || proposalsWithNextStep !== null || stalePipelineShare !== null || decisionMakerFirstMeeting !== null) && (
                      <div className="border border-border rounded-xl overflow-hidden">
                        <div className="bg-muted/40 px-4 py-3 border-b border-border">
                          <h3 className="font-semibold text-sm flex items-center gap-2">
                            <BarChart3 className="w-4 h-4" />
                            4. Operative Signale
                          </h3>
                          <p className="text-xs text-muted-foreground mt-0.5">Belastbarkeit des Vertriebsprozesses</p>
                        </div>
                        <div className="p-4 grid grid-cols-2 gap-3">
                          {[
                            { label: 'Qualifizierte Leads', value: qualifiedLeadRate, sub: 'Anteil ICP-passender Leads am Gesamteingang' },
                            { label: 'Angebote mit Next Step', value: proposalsWithNextStep, sub: 'Verbindlichkeit in Spätphasen des Funnels' },
                            { label: 'Stagnierende Pipeline', value: stalePipelineShare, sub: 'Deals ohne Bewegung (>14 Tage)', warn: stalePipelineShare !== null && stalePipelineShare > 30 },
                            { label: 'Entscheider im Erstgespräch', value: decisionMakerFirstMeeting, sub: 'Frühe Buying-Center-Einbindung' },
                          ].filter(k => k.value !== null).map(kpi => (
                            <div key={kpi.label} className={`rounded-lg p-3 ${(kpi as any).warn ? 'bg-amber-500/5 border border-amber-500/20' : 'bg-muted/50'}`}>
                              <p className="text-xs text-muted-foreground">{kpi.label}</p>
                              <p className={`text-xl font-black mt-1 ${(kpi as any).warn ? 'text-amber-700' : 'text-foreground'}`}>{pctFmt(kpi.value, 0)}</p>
                              <p className="text-xs text-muted-foreground mt-1">{kpi.sub}</p>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}

                    {/* ── Rep Execution Path ── */}
                    {r?.repExecutionPath && (
                      <div className="border border-border rounded-xl overflow-hidden">
                        <div className="bg-muted/40 px-4 py-3 border-b border-border">
                          <h3 className="font-semibold text-sm flex items-center gap-2">
                            <Users className="w-4 h-4" />
                            Rep Execution Path
                          </h3>
                          <p className="text-xs text-muted-foreground mt-0.5">Was jeder Rep konkret leisten muss</p>
                        </div>
                        <div className="p-4 space-y-4">
                          <div className="flex items-center justify-between">
                            <div className="text-sm">
                              <span className="text-muted-foreground">Produktive Reps:</span> <span className="font-bold">{r.repExecutionPath.productiveReps}</span>
                            </div>
                            <Badge variant="outline" className={
                              r.repExecutionPath.confidenceLabel === 'hoch' ? 'bg-emerald-500/10 text-emerald-700' :
                              r.repExecutionPath.confidenceLabel === 'mittel' ? 'bg-amber-500/10 text-amber-700' :
                              'bg-destructive/10 text-destructive'
                            }>
                              Konfidenz: {r.repExecutionPath.confidenceLabel}
                            </Badge>
                          </div>
                          <div className="grid grid-cols-2 gap-4">
                            <div className="bg-muted/50 rounded-lg p-3">
                              <p className="text-xs text-muted-foreground">Ziel-Umsatz / Rep / Monat</p>
                              <p className="text-lg font-bold">{formatCurrency(r.repExecutionPath.monthlyRevenuePerRepTarget)}</p>
                            </div>
                            <div className="bg-muted/50 rounded-lg p-3">
                              <p className="text-xs text-muted-foreground">Ist-Umsatz / Rep / Monat</p>
                              <p className="text-lg font-bold">{formatCurrency(r.repExecutionPath.monthlyRevenuePerRepCurrent)}</p>
                            </div>
                          </div>
                          <div className="space-y-2">
                            <p className="text-xs font-bold uppercase tracking-wider text-muted-foreground">Aktivitäten pro Rep / Monat</p>
                            <div className="grid grid-cols-4 gap-2 text-center">
                              <div className="bg-muted/30 rounded p-2">
                                <p className="text-[10px] text-muted-foreground">Kontakte</p>
                                <p className="font-bold">{formatNumber(r.repExecutionPath.monthlyContactsPerRepCurrent)}</p>
                                <p className="text-[10px] text-muted-foreground">Ziel: {formatNumber(r.repExecutionPath.monthlyContactsPerRepTarget)}</p>
                              </div>
                              <div className="bg-muted/30 rounded p-2">
                                <p className="text-[10px] text-muted-foreground">Discovery</p>
                                <p className="font-bold">{formatNumber(r.repExecutionPath.monthlyDiscoveryCallsPerRepCurrent)}</p>
                                <p className="text-[10px] text-muted-foreground">Ziel: {formatNumber(r.repExecutionPath.monthlyDiscoveryCallsPerRepTarget)}</p>
                              </div>
                              <div className="bg-muted/30 rounded p-2">
                                <p className="text-[10px] text-muted-foreground">Präsentationen</p>
                                <p className="font-bold">{formatNumber(r.repExecutionPath.monthlyPresentationsPerRepCurrent)}</p>
                                <p className="text-[10px] text-muted-foreground">Ziel: {formatNumber(r.repExecutionPath.monthlyPresentationsPerRepTarget)}</p>
                              </div>
                              <div className="bg-muted/30 rounded p-2">
                                <p className="text-[10px] text-muted-foreground">Abschlüsse</p>
                                <p className="font-bold">{formatNumber(r.repExecutionPath.monthlyDealsPerRepCurrent)}</p>
                                <p className="text-[10px] text-muted-foreground">Ziel: {formatNumber(r.repExecutionPath.monthlyDealsPerRepTarget)}</p>
                              </div>
                            </div>
                          </div>
                        </div>
                      </div>
                    )}

                    {/* ── Data Gaps ── */}
                    {r?.dataGaps && r.dataGaps.length > 0 && (
                      <div className="border border-border rounded-xl overflow-hidden">
                        <div className="bg-muted/40 px-4 py-3 border-b border-border">
                          <h3 className="font-semibold text-sm flex items-center gap-2">
                            <AlertCircle className="w-4 h-4 text-amber-500" />
                            Kritische Datenlücken
                          </h3>
                          <p className="text-xs text-muted-foreground mt-0.5">Fehlende Informationen, die die Analyse beeinträchtigen</p>
                        </div>
                        <div className="p-4 space-y-3">
                          {r.dataGaps.map((gap: any, idx: number) => (
                            <div key={idx} className="bg-amber-500/5 border border-amber-500/20 rounded-lg p-3">
                              <div className="flex items-center justify-between mb-1">
                                <p className="font-semibold text-sm text-amber-700">{gap.title}</p>
                                <Badge variant="outline" className="bg-amber-500/10 text-amber-700 border-amber-500/30 text-xs">
                                  {gap.severity}
                                </Badge>
                              </div>
                              <p className="text-xs text-muted-foreground mb-2">{gap.impact}</p>
                              <p className="text-xs font-medium text-foreground">Empfehlung: {gap.recommendation}</p>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}

                    {/* ── 5. Fokusfelder ── */}
                    {fp.length > 0 && (
                      <div className="border border-border rounded-xl overflow-hidden">
                        <div className="bg-muted/40 px-4 py-3 border-b border-border">
                          <h3 className="font-semibold text-sm flex items-center gap-2">
                            <Flame className="w-4 h-4 text-orange-500" />
                            5. Top-Fokusfelder
                          </h3>
                          <p className="text-xs text-muted-foreground mt-0.5">Wo sollte zuerst angesetzt werden</p>
                        </div>
                        <div className="p-4 space-y-3">
                          {fp.slice(0, 3).map((f: any, i: number) => (
                            <div key={i} className="border border-border rounded-xl p-4">
                              <div className="flex items-start gap-3">
                                <div className={`w-6 h-6 rounded-full flex-shrink-0 flex items-center justify-center text-xs font-black text-white ${rankColors[i] || 'bg-muted'}`}>{f.rank || i + 1}</div>
                                <div className="flex-1">
                                  <p className="font-bold text-sm">{f.title}</p>
                                  <p className="text-xs text-muted-foreground mt-1">{f.summary}</p>
                                  {f.rationale && <p className="text-xs text-primary mt-1 italic">{f.rationale}</p>}
                                </div>
                              </div>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}
                  </>
                );
              })()}
              </TabsContent>

              <TabsContent value="recommendations" className="space-y-6">
              {(() => {
                const r = selectedSubmission.result as any;
                const actionPlan = (() => { try { return buildActionPlanEngine(r); } catch { return null; } })();
                const narrative = (() => { try { return buildSalesNarrative(r); } catch { return null; } })();
                const masterData = (() => { try { return buildRecommendationMasterData(r); } catch { return null; } })();
                const hero = r?.views?.hero;
                const monthlyLoss = hero?.monthlyRevenueGap ?? r?.computedMetrics?.monthlyRevenueGap ?? 0;
                const annualLoss = hero?.annualRevenueGap ?? r?.computedMetrics?.annualRevenueGap ?? 0;
                const tp = r?.teamPerformance;
                const focusFields = (actionPlan?.priorityPlans || []).slice(0, 3);
                const quickWinsList = (actionPlan?.quickWins || []).slice(0, 5);
                const eurFmt = (v?: number | null) => formatCurrency(v ?? 0);
                const rankColors = ['bg-red-500','bg-amber-500','bg-emerald-500'];

                const hasPacing = !!tp?.summarySignals?.hasPacingProblem;
                const hasTargetBreakdown = !!tp?.summarySignals?.hasTargetBreakdownProblem;
                const hasPipeline = !!tp?.summarySignals?.hasPipelineProblem;
                const hasVariance = !!tp?.summarySignals?.hasVarianceProblem;
                const hasReview = !!tp?.summarySignals?.hasReviewProblem;

                const phase1Actions: string[] = hasPacing
                  ? [`Monatliches Pace-Review: Jeder Rep kennt sein Soll (${eurFmt(tp?.targetPerRepMonth)}/Monat) und seinen Ist-Stand.`, 'Wöchentliches Pipeline-Review: Welche Deals schließen diese Woche? Was blockiert?']
                  : hasTargetBreakdown
                  ? ['Jahresziel auf Rep-Ebene herunterbrechen und verbindlich kommunizieren.', 'Rep-Performance-Dashboard einführen: Jeder Rep sieht Ziel, Ist-Stand und Gap.']
                  : ['Lead-Owner und Reaktionsstandard verbindlich einführen', 'Erstkontakt und Next Step im CRM verpflichtend dokumentieren', 'Rep-Performance-Transparenz etablieren (Ziel vs. Ist)'];
                if (hasPipeline && hasPacing) { phase1Actions.push('Pipeline-Audit: Welche Opportunities sind realistisch?', 'Mindest-Pipeline-Coverage definieren und pro Rep tracken.'); }
                const phase2Actions: string[] = hasVariance
                  ? ['Top-Performer-Analyse: Was machen die besten 20% anders?', 'Best Practices dokumentieren und als Standard ausrollen.', 'Discovery-Template verbindlich einführen: Kein Angebot ohne dokumentierte Discovery.']
                  : ['Discovery-Template verbindlich ausrollen', 'No-Proposal-without-Qualification-Regel einführen', 'Peer-Learning-Sessions etablieren'];
                const phase3Actions: string[] = hasReview
                  ? ['Wöchentliches 1:1 mit jedem Rep: Pipeline-Review, Deal-Coaching, Blocker-Beseitigung.', 'Monatliches Team-Review: Pace vs. Plan, Insights, Lessons Learned.']
                  : ['Follow-up-Sequenzen standardisieren: Kein Deal ohne strukturierten Follow-up-Plan.'];
                if (hasPacing) { phase3Actions.push(`Monatliches Pace-Tracking: Ist das Team auf Kurs, den Gap von ${eurFmt(tp?.projectedYearEndGap)} zu schließen?`); }
                phase3Actions.push('Best Practices dokumentieren und teamweit verankern.');

                return (
                  <>
                    {/* ── DARK HERO BLOCK (1:1 wie ActionRecommendationsMasterAI) ── */}
                    <div className="rounded-3xl overflow-hidden shadow-2xl mb-2">

                      {/* Oberer Teil: Dunkel, volle Breite */}
                      <div className="relative bg-slate-950 px-7 py-9 md:px-10 md:py-12 overflow-hidden">
                        {/* Hintergrund-Akzente */}
                        <div className="pointer-events-none absolute -top-24 -right-24 h-72 w-72 rounded-full bg-red-600/10 blur-3xl" />
                        <div className="pointer-events-none absolute -bottom-16 -left-16 h-56 w-56 rounded-full bg-blue-600/10 blur-3xl" />

                        {/* Status-Pill */}
                        <div className="inline-flex items-center gap-2 rounded-full border border-red-500/40 bg-red-500/15 px-4 py-1.5 text-xs font-bold uppercase tracking-wider text-red-400 mb-6">
                          <span className="relative flex h-2 w-2">
                            <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-red-400 opacity-75" />
                            <span className="relative inline-flex h-2 w-2 rounded-full bg-red-500" />
                          </span>
                          Audit abgeschlossen — Handlungsbedarf identifiziert
                        </div>

                        {/* Headline */}
                        <h2 className="text-3xl md:text-4xl font-black text-white leading-tight tracking-tight max-w-3xl mb-4">
                          {selectedSubmission.companyProfile?.companyName
                            ? <>{selectedSubmission.companyProfile.companyName}:<br className="hidden md:block" /> <span className="text-red-400">3 Engpässe kosten jeden Monat Umsatz.</span></>
                            : <>Ihr Vertrieb hat <span className="text-red-400">3 strukturelle Engpässe.</span></>
                          }
                        </h2>

                        {/* Executive Summary Text */}
                        <p className="text-base text-slate-400 leading-relaxed max-w-2xl">
                          {(narrative?.hero?.headline
                            ? (narrative?.rootCauseDetail || narrative?.rootCause || narrative.hero.headline)
                            : masterData?.dominantBottleneck?.title
                            ? `${masterData.dominantBottleneck.title} ist der kritischste Engpass.`
                            : null)
                          || (hasPacing
                            ? `Das Team liegt unter dem linearen Sollverlauf. Bei aktueller Pace entsteht bis Jahresende eine Ziellücke von ${eurFmt(tp?.projectedYearEndGap)}.`
                            : hasTargetBreakdown
                            ? `Der größte Umsatzverlust entsteht durch fehlende Zielsteuerung. Aktuell bleiben rund ${eurFmt(monthlyLoss)} pro Monat unrealisiert.`
                            : `Die Lücke entsteht nicht durch mangelnde Nachfrage — sondern durch fehlende Struktur, unklare Ownership und fehlende Verbindlichkeit.`
                          )}
                        </p>

                        {/* KPI-Zeile: Jahres-Lücke + monatlicher Verlust */}
                        {(annualLoss > 0 || monthlyLoss > 0) && (
                          <div className="mt-7 flex flex-wrap gap-4">
                            {annualLoss > 0 && (
                              <div className="rounded-2xl border border-red-500/30 bg-red-500/10 px-6 py-4">
                                <div className="text-[10px] font-bold uppercase tracking-wider text-red-400/70 mb-1">Projizierte Jahres-Lücke</div>
                                <div className="text-3xl font-black text-red-400">{eurFmt(annualLoss)}</div>
                                <div className="text-xs text-red-400/60 mt-0.5">ungenutzte Erlöse bis Jahresende</div>
                              </div>
                            )}
                            {monthlyLoss > 0 && (
                              <div className="rounded-2xl border border-amber-500/30 bg-amber-500/10 px-6 py-4">
                                <div className="text-[10px] font-bold uppercase tracking-wider text-amber-400/70 mb-1">Verlust je Monat Verzögerung</div>
                                <div className="text-3xl font-black text-amber-400">{eurFmt(monthlyLoss)}</div>
                                <div className="text-xs text-amber-400/60 mt-0.5">solange die Engpässe offen bleiben</div>
                              </div>
                            )}
                          </div>
                        )}

                        {/* One-Thing / Signatur-Satz */}
                        {narrative?.oneThing && (
                          <div className="mt-6 flex items-start gap-3 rounded-2xl border border-blue-500/25 bg-blue-500/8 px-5 py-4">
                            <Zap className="h-5 w-5 text-blue-400 shrink-0 mt-0.5" />
                            <p className="text-sm text-blue-200 leading-relaxed italic">{narrative.oneThing}</p>
                          </div>
                        )}
                      </div>

                      {/* Unterer Teil: Die 3 Engpässe als Prioritätsliste */}
                      {focusFields.length > 0 && (
                        <div className="bg-slate-900 border-t border-white/5 px-7 pt-6 pb-5 md:px-10">
                          <div className="flex items-center justify-between mb-4">
                            <span className="text-[10px] font-bold uppercase tracking-[0.18em] text-slate-500">Die 3 Engpässe — priorisierte Reihenfolge</span>
                            <span className="text-[10px] text-slate-600 font-medium">Fokus 1 → 2 → 3</span>
                          </div>
                          <div className="space-y-3">
                            {focusFields.map((f: any, i: number) => (
                              <div key={i} className="flex items-center gap-4">
                                <div className={`flex h-8 w-8 shrink-0 items-center justify-center rounded-full text-sm font-black ${
                                  i === 0 ? 'bg-red-500 text-white shadow-lg shadow-red-500/30' : 'bg-slate-800 text-slate-400'
                                }`}>{i + 1}</div>
                                <div className="flex-1 min-w-0">
                                  <div className="flex items-center justify-between gap-3 flex-wrap">
                                    <p className={`text-sm font-bold ${i === 0 ? 'text-white' : 'text-slate-300'}`}>{f.title}</p>
                                    {(f.revenueLeakMonthly || 0) > 0 && (
                                      <span className="text-xs font-bold text-red-400 bg-red-500/10 border border-red-500/20 px-2 py-0.5 rounded-full shrink-0">
                                        −{eurFmt(f.revenueLeakMonthly)}/Monat
                                      </span>
                                    )}
                                  </div>
                                  {f.whyNow && <p className="text-xs text-slate-500 mt-0.5 truncate">{f.whyNow}</p>}
                                </div>
                              </div>
                            ))}
                          </div>
                        </div>
                      )}
                    </div>

                    {/* Download-Buttons */}
                    <div className="flex gap-3 justify-end">
                      <Button variant="outline" className="gap-2" onClick={() => {
                        const rd = buildReportDataFromSubmission(selectedSubmission);
                        if (!rd) { toast.error('Report-Daten konnten nicht geladen werden'); return; }
                        toast.success('PDF-Report wird generiert...');
                        downloadFullReport(rd);
                      }}>
                        <FileDown className="w-4 h-4" />
                        PDF-Report
                      </Button>
                      <Button variant="outline" className="gap-2" onClick={() => {
                        const rd = buildReportDataFromSubmission(selectedSubmission);
                        if (!rd) { toast.error('Report-Daten konnten nicht geladen werden'); return; }
                        toast.success('PowerPoint wird generiert...');
                        downloadPptx(rd);
                      }}>
                        <Layers className="w-4 h-4" />
                        PowerPoint
                      </Button>
                    </div>

                    {/* Top-3 Fokusfelder mit Maßnahmen */}
                    {focusFields.length > 0 && (
                      <div className="border border-border rounded-xl overflow-hidden">
                        <div className="bg-muted/40 px-4 py-3 border-b border-border">
                          <h3 className="font-semibold text-sm flex items-center gap-2">
                            <Target className="w-4 h-4 text-primary" />
                            Top-3 Engpassfelder &amp; Maßnahmen
                          </h3>
                          <p className="text-xs text-muted-foreground mt-0.5">Datengetriebene Prioritäten mit konkreten Maßnahmen aus der Audit-Engine</p>
                        </div>
                        <div className="p-4 space-y-4">
                          {focusFields.map((f: any, idx: number) => (
                            <div key={idx} className={`rounded-xl border p-4 space-y-3 ${
                              idx === 0 ? 'border-red-200 bg-red-50/30 dark:bg-red-950/20 dark:border-red-800' :
                              idx === 1 ? 'border-amber-200 bg-amber-50/30 dark:bg-amber-950/20 dark:border-amber-800' :
                              'border-emerald-200 bg-emerald-50/30 dark:bg-emerald-950/20 dark:border-emerald-800'
                            }`}>
                              <div className="flex items-start gap-3">
                                <div className={`w-7 h-7 rounded-full ${rankColors[idx]} text-white text-xs font-black flex items-center justify-center flex-shrink-0`}>{idx + 1}</div>
                                <div className="flex-1 min-w-0">
                                  <p className="font-bold text-sm">{f.title}</p>
                                  {f.whyNow && <p className="text-xs text-muted-foreground mt-0.5 italic">{f.whyNow}</p>}
                                  {(f.revenueLeakMonthly || 0) > 0 && (
                                    <span className="inline-block mt-1 text-xs font-bold text-destructive bg-destructive/10 px-2 py-0.5 rounded">
                                      Umsatzleck: {eurFmt(f.revenueLeakMonthly)}/Monat
                                    </span>
                                  )}
                                </div>
                              </div>
                              {f.problem && <p className="text-xs text-muted-foreground pl-10">{f.problem}</p>}
                              {f.rootCause && (
                                <div className="pl-10">
                                  <span className="text-xs font-semibold text-foreground">Root Cause: </span>
                                  <span className="text-xs text-muted-foreground">{f.rootCause}</span>
                                </div>
                              )}
                              {(f.expectedShortTermImpact || f.shortTermImpact) && (
                                <div className="pl-10 rounded-lg bg-primary/5 border border-primary/10 px-3 py-2">
                                  <p className="text-xs font-semibold text-primary">📈 {f.expectedShortTermImpact || f.shortTermImpact}</p>
                                </div>
                              )}
                              {/* Maßnahmen (Tasks) */}
                              {Array.isArray(f.tasks) && f.tasks.length > 0 && (
                                <div className="pl-10">
                                  <p className="text-xs font-bold text-foreground mb-1.5">Konkrete Maßnahmen:</p>
                                  <div className="space-y-2">
                                    {f.tasks.slice(0, 4).map((task: any, ti: number) => (
                                      <div key={ti} className="bg-background/80 border border-border rounded-lg p-2.5">
                                        <p className="text-xs font-semibold">{typeof task === 'string' ? task : (task.title || task)}</p>
                                        {task.steps && Array.isArray(task.steps) && task.steps.length > 0 && (
                                          <ul className="mt-1.5 space-y-0.5">
                                            {task.steps.slice(0, 3).map((step: string, si: number) => (
                                              <li key={si} className="text-xs text-muted-foreground flex gap-1.5">
                                                <span className="text-primary mt-0.5">›</span>
                                                <span>{step}</span>
                                              </li>
                                            ))}
                                          </ul>
                                        )}
                                        <div className="flex gap-3 mt-1.5 flex-wrap">
                                          {task.owner && <span className="text-xs text-muted-foreground"><strong>Owner:</strong> {typeof task.owner === 'string' ? task.owner : (task.owner?.role || String(task.owner))}</span>}
                                          {task.dueInDays && <span className="text-xs text-muted-foreground"><strong>Frist:</strong> {task.dueInDays} Tage</span>}
                                          {task.successMetric && <span className="text-xs text-muted-foreground"><strong>KPI:</strong> {task.successMetric}</span>}
                                        </div>
                                      </div>
                                    ))}
                                  </div>
                                </div>
                              )}
                              {(!f.tasks || f.tasks.length === 0) && Array.isArray(f.actions) && f.actions.length > 0 && (
                                <ul className="pl-10 space-y-1">
                                  {f.actions.slice(0, 4).map((a: string, ai: number) => (
                                    <li key={ai} className="text-xs text-muted-foreground flex gap-1.5">
                                      <span className="text-primary mt-0.5">›</span>
                                      <span>{a}</span>
                                    </li>
                                  ))}
                                </ul>
                              )}
                            </div>
                          ))}
                        </div>
                      </div>
                    )}

                    {/* Quick Wins */}
                    {quickWinsList.length > 0 && (
                      <div className="border border-border rounded-xl overflow-hidden">
                        <div className="bg-emerald-50 dark:bg-emerald-950/40 px-4 py-3 border-b border-emerald-200 dark:border-emerald-800">
                          <h3 className="font-semibold text-sm flex items-center gap-2 text-emerald-800 dark:text-emerald-200">
                            <Zap className="w-4 h-4" />
                            Quick Wins — Sofortmaßnahmen (14 Tage)
                          </h3>
                          <p className="text-xs text-emerald-700 dark:text-emerald-300 mt-0.5">Maßnahmen mit schnell sichtbarem Effekt und klarem Owner</p>
                        </div>
                        <div className="p-4 space-y-3">
                          {quickWinsList.map((qw: any, qi: number) => (
                            <div key={qi} className="bg-emerald-50/60 dark:bg-emerald-950/20 border border-emerald-200 dark:border-emerald-800 rounded-xl p-4">
                              <p className="font-bold text-sm text-emerald-900 dark:text-emerald-100">{qw.title}</p>
                              {(qw.whyItWorks || qw.description) && (
                                <p className="text-xs text-emerald-700 dark:text-emerald-300 mt-1.5 leading-relaxed">{qw.whyItWorks || qw.description}</p>
                              )}
                              {qw.implementation && (
                                <p className="text-xs text-muted-foreground mt-1.5"><span className="font-semibold text-foreground">Umsetzung:</span> {qw.implementation}</p>
                              )}
                              {Array.isArray(qw.steps) && qw.steps.length > 0 && (
                                <ul className="mt-1.5 space-y-0.5">
                                  {qw.steps.slice(0, 3).map((step: string, si: number) => (
                                    <li key={si} className="text-xs text-muted-foreground flex gap-1.5">
                                      <span className="text-emerald-600 mt-0.5">›</span>
                                      <span>{step}</span>
                                    </li>
                                  ))}
                                </ul>
                              )}
                              <div className="flex gap-4 mt-2.5 flex-wrap">
                                {qw.owner && <span className="text-xs text-muted-foreground"><strong>Owner:</strong> {typeof qw.owner === 'string' ? qw.owner : (qw.owner?.role || String(qw.owner))}</span>}
                                {(qw.dueInDays || qw.deadline) && <span className="text-xs text-muted-foreground"><strong>Frist:</strong> {qw.dueInDays ? `${qw.dueInDays} Tage` : qw.deadline}</span>}
                                {qw.successMetric && <span className="text-xs text-muted-foreground"><strong>KPI:</strong> {qw.successMetric}</span>}
                              </div>
                              {qw.expectedImpact && (
                                <div className="mt-2 bg-emerald-100 dark:bg-emerald-900/50 rounded px-2 py-1.5">
                                  <p className="text-xs font-semibold text-emerald-900 dark:text-emerald-200">💡 {qw.expectedImpact}</p>
                                </div>
                              )}
                            </div>
                          ))}
                        </div>
                      </div>
                    )}

                    {/* 30/60/90-Tage-Plan */}
                    <div className="border border-border rounded-xl overflow-hidden">
                      <div className="bg-muted/40 px-4 py-3 border-b border-border">
                        <h3 className="font-semibold text-sm flex items-center gap-2">
                          <Clock className="w-4 h-4" />
                          30/60/90-Tage Umsetzungsplan
                        </h3>
                        <p className="text-xs text-muted-foreground mt-0.5">Priorisierter Maßnahmenplan basierend auf den Audit-Daten</p>
                      </div>
                      <div className="p-4 grid gap-4 md:grid-cols-3">
                        {[
                          { phase: '0–30 Tage', goal: hasPacing ? 'Pace-Gap stoppen und monatlichen Aufholbedarf aktivieren.' : hasTargetBreakdown ? 'Ziel-Transparenz und Ownership schaffen.' : 'Kritische Leckagen schließen und Transparenz schaffen.', focus: hasPacing ? 'Pace, Steuerbarkeit, Forecast' : 'Ownership, Sichtbarkeit, Quick Wins', color: 'bg-red-50 dark:bg-red-950/20 border-red-200 dark:border-red-800', textColor: 'text-red-700 dark:text-red-300', actions: phase1Actions.slice(0, 4) },
                          { phase: '30–60 Tage', goal: hasVariance ? 'Top-Performer-Verhalten standardisieren und skalieren.' : 'Qualifizierung und Opportunity-Qualität stabilisieren.', focus: hasVariance ? 'Best Practices, Standardisierung, Peer-Learning' : 'Discovery, Bedarfstiefe, Angebotsreife', color: 'bg-amber-50 dark:bg-amber-950/20 border-amber-200 dark:border-amber-800', textColor: 'text-amber-700 dark:text-amber-300', actions: phase2Actions.slice(0, 4) },
                          { phase: '60–90 Tage', goal: hasReview ? 'Review-Rhythmus und Steuerungsdisziplin verankern.' : 'Follow-up, Forecast und Abschlusslogik skalierbar machen.', focus: 'Verbindlichkeit, Nachverfolgung, Steuerbarkeit', color: 'bg-emerald-50 dark:bg-emerald-950/20 border-emerald-200 dark:border-emerald-800', textColor: 'text-emerald-700 dark:text-emerald-300', actions: phase3Actions.slice(0, 4) },
                        ].map(ph => (
                          <div key={ph.phase} className={`rounded-xl border p-4 ${ph.color}`}>
                            <p className={`text-xs font-black uppercase tracking-wider mb-1 ${ph.textColor}`}>{ph.phase}</p>
                            <p className="font-semibold text-sm text-foreground mb-1">{ph.goal}</p>
                            <p className="text-xs text-muted-foreground mb-3 italic">{ph.focus}</p>
                            <ul className="space-y-1.5">
                              {ph.actions.map((a, ai) => (
                                <li key={ai} className="text-xs flex gap-1.5">
                                  <span className={`mt-0.5 flex-shrink-0 ${ph.textColor}`}>›</span>
                                  <span className="text-foreground/80">{a}</span>
                                </li>
                              ))}
                            </ul>
                          </div>
                        ))}
                      </div>
                    </div>
                  </>
                );
              })()}
              </TabsContent>
            </Tabs>
          )}
        </DialogContent>
      </Dialog>

      {/* Note Dialog */}
      <Dialog open={showNoteDialog} onOpenChange={setShowNoteDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Notiz hinzufügen</DialogTitle>
            <DialogDescription>
              Fügen Sie eine Notiz zu dieser Submission hinzu
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <Label htmlFor="note">Notiz</Label>
              <Textarea
                id="note"
                placeholder="Ihre Notiz..."
                value={newNote}
                onChange={(e) => setNewNote(e.target.value)}
                rows={4}
              />
            </div>
            <div className="flex gap-2 justify-end">
              <Button variant="outline" onClick={() => setShowNoteDialog(false)}>
                Abbrechen
              </Button>
              <Button onClick={handleAddNote} disabled={!newNote.trim()}>
                <Plus className="w-4 h-4 mr-2" />
                Hinzufügen
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* CSV Export Dialog */}
      <Dialog open={showCSVDialog} onOpenChange={setShowCSVDialog}>
        <DialogContent className="max-w-4xl max-h-[80vh]">
          <DialogHeader>
            <DialogTitle>📄 CSV-Daten</DialogTitle>
            <DialogDescription>
              Markieren Sie den gesamten Text unten (Strg+A) und kopieren Sie ihn (Strg+C).
              Dann in Excel einfügen (Strg+V).
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div className="relative">
              <textarea
                value={csvContent}
                readOnly
                className="w-full h-96 p-4 font-mono text-sm border rounded-lg bg-muted"
                onClick={(e) => {
                  const target = e.target as HTMLTextAreaElement;
                  target.select();
                }}
              />
              <div className="absolute top-2 right-2">
                <Button
                  size="sm"
                  variant="outline"
                  onClick={() => {
                    const textarea = document.querySelector('textarea');
                    if (textarea) {
                      textarea.select();
                      document.execCommand('copy');
                      toast.success('✅ In Zwischenablage kopiert!');
                    }
                  }}
                >
                  <Copy className="w-4 h-4 mr-2" />
                  Kopieren
                </Button>
              </div>
            </div>
            <div className="bg-blue-50 dark:bg-blue-950 p-4 rounded-lg">
              <p className="text-sm font-semibold mb-2">💡 Anleitung:</p>
              <ol className="text-sm space-y-1 list-decimal list-inside">
                <li>Klicken Sie auf "Kopieren" oder markieren Sie alles (Strg+A)</li>
                <li>Kopieren Sie den Text (Strg+C)</li>
                <li>Öffnen Sie Excel</li>
                <li>Fügen Sie ein (Strg+V)</li>
                <li>Falls nötig: Daten → Text in Spalten → Semikolon</li>
              </ol>
            </div>
            <div className="flex justify-end">
              <Button onClick={() => setShowCSVDialog(false)}>
                Schließen
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
      <AdminHelp />
    </Layout>
  );
}